import { useEffect, useLayoutEffect, useRef, useState, useCallback, memo, useMemo } from "react";
import { Play, Pause, Volume2, Wind, Music2, Waves } from "lucide-react";

import TopbarInner from "../components/topbar/TopbarInner";
import BreathingAnimation from "../components/BreathingAnimation";
import Tooltip from "../components/Tooltip";
import BadgeCelebration from "../components/BadgeCelebration";
import { audioManager } from "../utils/AudioManager";
import { armMediaGroup, pauseAndResetMedia, startMediaGroup } from "../utils/mediaStart";
import { resolveAudioSource } from "../services/premiumAudio.js";
import { tooltipManager } from "../utils/tooltipManager";
import { doc, setDoc } from "firebase/firestore";
import { db } from "../firebase-config";

const BREATH_VOL = 1;

const NATURAL_GAIN = 0.562;

const SESSION_LIMIT_SECONDS = {
  "30sec": 30,
  "48sec": 48,
  "2min": 120,
  "5min": 300,
};

const LIMIT_EPS = 0.05;

const START_FADE_MS = 700;
const BREATHING_START_FADE_MS = 260;
const REVEAL_REANCHOR_TOLERANCE = 0.06;
const FORCED_SYNC_TOLERANCE = 0.04;
const AMBIANCE_START_FADE_MS = 500;
const PAUSE_FADE_MS = 200;
const RESUME_FADE_MS = 220;
const FINAL_FADE_MS = 2000;
const FINAL_FADE_LEAD_SECONDS = FINAL_FADE_MS / 1000;

const AUTO_FULLSCREEN_IDLE_MS = 22000;

const getBaseVolumeKey = (uid) => `harmonauria_base_volume:${uid || "guest"}`;

const MODE_CROSSFADE_MS = 420;
const MODE_SYNC_TOLERANCE = 0.08;
const PLAYER_TOOLTIP_IDS = Object.freeze(["breathing-fullscreen-v1", "breathing-mute", "base-volume-memory"]);

function waitUntilPlayable(element, timeoutMs = 8000) {
  return new Promise((resolve, reject) => {
    if (!element) {
      reject(new Error("Élément audio absent"));
      return;
    }

    if (element.readyState >= 3) {
      resolve();
      return;
    }

    let timeoutId;

    const cleanup = () => {
      window.clearTimeout(timeoutId);
      element.removeEventListener("loadeddata", handleReady);
      element.removeEventListener("canplay", handleReady);
      element.removeEventListener("error", handleError);
    };

    const handleReady = () => {
      if (element.readyState < 3) return;
      cleanup();
      resolve();
    };

    const handleError = () => {
      cleanup();
      reject(new Error("Impossible de charger la nouvelle ambiance"));
    };

    timeoutId = window.setTimeout(() => {
      cleanup();
      reject(new Error("Le chargement de la nouvelle ambiance a expiré"));
    }, timeoutMs);

    element.addEventListener("loadeddata", handleReady, { once: true });
    element.addEventListener("canplay", handleReady, { once: true });
    element.addEventListener("error", handleError, { once: true });
  });
}

const NaturalSoundTile = memo(function NaturalSoundTile({
  sound,
  active,
  loading = false,
  onToggle,
  onVolumeChange,
  isPremium = false,
  onLockedClick,
  onGuideInteracted,
  initialVolume = 0.7,
}) {
  const volumeRef = useRef(initialVolume);
  const lastClickRef = useRef(0);

  useEffect(() => {
    volumeRef.current = initialVolume;
  }, [initialVolume]);

  const isLocked = sound.premium && !isPremium;

  const handleToggle = useCallback((event) => {
    if (loading) return;

    const now = Date.now();
    if (now - lastClickRef.current < 300) return;

    lastClickRef.current = now;
    event.currentTarget.blur();
    onGuideInteracted?.();

    if (isLocked) {
      onLockedClick?.(sound.id);
      return;
    }

    onToggle(sound.id, volumeRef.current);
  }, [isLocked, loading, onGuideInteracted, onLockedClick, onToggle, sound.id]);

const handleVolumeInput = useCallback((event) => {
  const nextVolume = Math.max(0, Math.min(1, Number(event.currentTarget.value) || 0));

  volumeRef.current = nextVolume;
  onGuideInteracted?.();
  onVolumeChange(sound.id, nextVolume);
}, [onGuideInteracted, onVolumeChange, sound.id]);

  return (
    <div className="natural-sound-item">
<button
  type="button"
  onClick={handleToggle}
  disabled={loading}
  aria-busy={loading}
  className={`natural-sound-button ${active ? "active" : ""} ${isLocked ? "locked" : ""}`}
  aria-pressed={active}
  aria-disabled={isLocked}
  aria-label={isLocked ? `${sound.name} — réservé au Refuge Personnel` : sound.name}
  data-locked={isLocked}
>
{isLocked && (
  <span className="natural-sound-lock" aria-hidden="true">
    <span>Refuge</span>
  </span>
)}

        <div className="sound-emoji-circle">
          <img
            src={sound.image}
            alt={sound.name}
            className="sound-icon-img"
            onError={(event) => {
              console.error(`❌ Erreur chargement image: ${sound.image}`);
              event.currentTarget.style.display = "none";
            }}
          />
        </div>

        <span className="sound-name">{sound.name}</span>

        {(loading || active) && (
          <span className="sound-indicator" aria-hidden="true">
            {loading ? "⏳" : <Volume2 size={12} />}
          </span>
        )}
      </button>

      {active && !isLocked && (
        <div className="volume-control">
          <Volume2 size={14} className="volume-icon" />
          <input
            type="range"
            min={0}
            max={1}
            step={0.01}
            defaultValue={initialVolume}
            onInput={handleVolumeInput}
            className="volume-slider"
          />
        </div>
      )}
    </div>
  );
});

function markPlayerTourAsSeen(uid) {
  if (!uid) return;
  setDoc(
    doc(db, "users", uid),
    { guidesSeen: { playerTour: true } },
    { merge: true }
  ).catch((e) => console.warn("markPlayerTourAsSeen failed:", e));
}

function PlayerSpotlight({ targetId, padding = 16 }) {
  const [rect, setRect] = useState(null);

  useEffect(() => {
    if (!targetId) {
      setRect(null);
      return;
    }

    let rafId = 0;

    const update = () => {
      const el = document.getElementById(targetId);
      if (!el) {
        setRect(null);
        return;
      }
      const r = el.getBoundingClientRect();
      setRect({
        top: r.top + window.scrollY - padding,
        left: r.left + window.scrollX - padding,
        width: r.width + padding * 2,
        height: r.height + padding * 2,
      });
    };

    const scheduleUpdate = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        update();
      });
    };

    update();

    window.addEventListener("resize", scheduleUpdate);

    let ro;
    const el = document.getElementById(targetId);
    if (el && "ResizeObserver" in window) {
      ro = new ResizeObserver(scheduleUpdate);
      ro.observe(el);
    }

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener("resize", scheduleUpdate);
      ro?.disconnect();
    };
  }, [targetId, padding]);

  if (!rect) return null;

  return (
    <div className="pt-spotlight-layer" aria-hidden="true">
      <div
        className="pt-spotlight"
        style={{
          top: `${rect.top}px`,
          left: `${rect.left}px`,
          width: `${rect.width}px`,
          height: `${rect.height}px`,
        }}
      />
    </div>
  );
}

function PlayerTour({ user, naturalSoundTouched, isAudioReady, demoMode = false, forceReplay = false }) {
  const [phase, setPhase] = useState(null);
  const hasAlreadySeenPlayerTour = !demoMode && !forceReplay && user?.guidesSeen?.playerTour === true;

  const finishTour = useCallback(() => {
    if (!demoMode) {
      markPlayerTourAsSeen(user?.uid);
      tooltipManager.setJourneyStep("done");
    }

    tooltipManager.clearActiveTour("player_tour");
    setPhase("done");
  }, [demoMode, user?.uid]);

useEffect(() => {
  if (!isAudioReady || phase !== null) return undefined;

  if (hasAlreadySeenPlayerTour) {
    setPhase("done");
    return undefined;
  }

  if (demoMode || forceReplay) {
    setPhase("scroll");
    return undefined;
  }

  const startWhenTooltipsAreDone = () => {
    if (!tooltipManager.hasSeenAll(PLAYER_TOOLTIP_IDS)) return;
    tooltipManager.setActiveTour("player_tour");
    setPhase("scroll");
  };

  startWhenTooltipsAreDone();
  return tooltipManager.subscribeSeen(startWhenTooltipsAreDone);
}, [demoMode, forceReplay, hasAlreadySeenPlayerTour, isAudioReady, phase]);

  useEffect(() => () => tooltipManager.clearActiveTour("player_tour"), []);

useEffect(() => {
  if (phase !== "scroll") return;

  const el = document.getElementById("player-natural-sounds");
  if (!el) return;

  const isVisible = () => {
    const r = el.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    return r.top < vh - 100 && r.bottom > 100;
  };

  // Si la section est déjà visible au démarrage, on ne switch pas tout de suite :
  // on laisse le user voir la flèche, on switche seulement après un nouveau scroll.
  let initiallyVisible = isVisible();

  const onScroll = () => {
    if (initiallyVisible) {
      // On attend que le user scroll (et donc que la section soit "re-positionnée")
      initiallyVisible = false;
      return;
    }
    if (isVisible()) {
      setPhase("naturals");
    }
  };

  window.addEventListener("scroll", onScroll, { passive: true, capture: true });
  return () => window.removeEventListener("scroll", onScroll, true);
}, [phase]);

  // Ferme dès qu'un son est cliqué
  useEffect(() => {
    if (phase !== "naturals") return;
    if (!naturalSoundTouched) return;
    finishTour();
  }, [phase, naturalSoundTouched, finishTour]);

const scrollToNaturals = useCallback(() => {
  const el = document.getElementById("player-natural-sounds");
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "center" });
  // On passe à naturals après le scroll (laisse 700ms pour que ça arrive)
  setTimeout(() => setPhase("naturals"), 700);
}, []);

  if (phase == null || phase === "done") return null;

  return (
    <>
      {phase === "naturals" && (
        <PlayerSpotlight key="player-naturals" targetId="player-natural-sounds" padding={16} />
      )}

      {phase === "scroll" && (
        <button
          type="button"
          className="pt-scroll-hint"
          onClick={scrollToNaturals}
          aria-label="Faire défiler vers les sons naturels"
        >
          <span className="pt-scroll-label">
            Découvrez les sons naturels
          </span>
          <span className="pt-scroll-arrow" aria-hidden="true">↓</span>
        </button>
      )}

      {phase === "naturals" && (
        <div className="pt-dock" aria-live="polite">
          <div className="pt-card">
            <div className="pt-card__pill">
              <span className="pt-card__pill-dot" />
              Astuce
            </div>

            <div className="pt-card__icon" aria-hidden="true">🌿</div>

            <h3 className="pt-card__title">Personnalisez votre ambiance</h3>
            <p className="pt-card__text">
              Ajoutez un son naturel — pluie, océan, forêt — et réglez son volume
              pour créer une atmosphère sur mesure.
            </p>

            <button
              type="button"
              className="pt-card__action"
              onClick={finishTour}
            >
              J’ai compris
            </button>

            <button
              type="button"
              className="pt-card__close"
              onClick={finishTour}
              aria-label="Fermer"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      <style>{`
        #player-natural-sounds {
          scroll-margin-top: 110px;
        }

        /* ============ SPOTLIGHT ============ */
        .pt-spotlight-layer {
          position: absolute;
          top: 0;
          left: 0;
          z-index: 9988;
          pointer-events: none;
        }

        .pt-spotlight {
          position: absolute;
          border-radius: 22px;
          box-shadow:
            0 0 0 9999px rgba(15, 23, 42, 0.55),
            inset 0 0 0 1.5px rgba(255, 255, 255, 0.12),
            0 0 0 2px rgba(118, 165, 167, 0.55),
            0 0 36px rgba(118, 165, 167, 0.35);
          animation: ptSpotIn .35s ease-out both, ptSpotPulse 2.4s ease-in-out 0.35s infinite;
          will-change: box-shadow;
        }

        /* ============ SCROLL HINT ============ */
        .pt-scroll-hint {
          position: fixed;
          left: 50%;
          bottom: calc(env(safe-area-inset-bottom, 0px) + 28px);
          transform: translateX(-50%);
          z-index: 9990;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 10px;
          border: none;
          background: transparent;
          cursor: pointer;
          animation: ptFadeIn .4s ease-out both;
        }

        .pt-scroll-label {
          font-size: 13px;
          font-weight: 800;
          color: #36495B;
          background: rgba(255,255,255,.96);
          border: 1px solid rgba(118,165,167,.22);
          border-radius: 999px;
          padding: 9px 18px;
          box-shadow: 0 12px 28px rgba(15,23,42,.12);
          white-space: nowrap;
          backdrop-filter: blur(12px);
        }

        .pt-scroll-arrow {
          font-size: 22px;
          color: #61837A;
          line-height: 1;
          width: 36px;
          height: 36px;
          border-radius: 999px;
          display: grid;
          place-items: center;
          background: linear-gradient(135deg, #ffffff, #e8f5f2);
          border: 1px solid rgba(118,165,167,.28);
          box-shadow: 0 6px 16px rgba(118,165,167,.30);
          animation: ptBounce 1.4s ease-in-out infinite;
        }

        /* ============ DOCK / CARD ============ */
        .pt-dock {
          position: fixed;
          left: 50%;
          bottom: calc(env(safe-area-inset-bottom, 0px) + 22px);
          transform: translateX(-50%);
          z-index: 9991;
          width: min(360px, calc(100vw - 24px));
          pointer-events: none;
        }

        .pt-card {
          position: relative;
          pointer-events: auto;
          text-align: center;
          padding: 20px 20px 18px;
          border-radius: 24px;
          background: linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,251,250,0.98) 100%);
          border: 1px solid rgba(118,165,167,0.22);
          box-shadow:
            0 28px 64px rgba(15,23,42,0.22),
            0 4px 18px rgba(118,165,167,0.10),
            inset 0 1px 0 rgba(255,255,255,0.95);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          animation: ptCardIn .42s cubic-bezier(.34,1.56,.64,1) both;
        }

        .pt-card__pill {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 5px 12px 5px 10px;
          margin-bottom: 12px;
          border-radius: 999px;
          font-size: 11px;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: .06em;
          color: #61837A;
          background: linear-gradient(135deg, rgba(203,226,219,0.55), rgba(118,165,167,0.18));
          border: 1px solid rgba(118,165,167,0.30);
        }

        .pt-card__pill-dot {
          display: inline-block;
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #61837A;
          box-shadow: 0 0 0 3px rgba(97,131,122,0.20);
          animation: ptPillPulse 1.6s ease-in-out infinite;
        }

        .pt-card__icon {
          font-size: 32px;
          line-height: 1;
          margin-bottom: 10px;
          display: inline-block;
          filter: drop-shadow(0 4px 10px rgba(118,165,167,0.30));
          animation: ptIconBob 2.6s ease-in-out infinite;
        }

        .pt-card__title {
          margin: 0 0 6px;
          font-family: "Nunito", system-ui, sans-serif;
          font-size: 16.5px;
          font-weight: 800;
          color: #36495B;
          letter-spacing: -0.015em;
          line-height: 1.3;
        }

        .pt-card__text {
          margin: 0 0 16px;
          font-size: 13.5px;
          font-weight: 500;
          line-height: 1.5;
          color: rgba(54,73,91,0.72);
        }

        .pt-card__action {
          width: 100%;
          padding: 11px 16px;
          border: none;
          border-radius: 14px;
          background: linear-gradient(135deg, #61837A 0%, #76A5A7 100%);
          color: #fff;
          font-family: var(--font-sans);
          font-size: 13.5px;
          font-weight: 800;
          letter-spacing: 0.01em;
          cursor: pointer;
          box-shadow: 0 10px 22px rgba(97,131,122,0.32), inset 0 1px 0 rgba(255,255,255,0.18);
          transition: transform .15s ease, box-shadow .15s ease, filter .15s ease;
        }

        .pt-card__action:hover {
          transform: translateY(-1px);
          filter: brightness(1.04);
          box-shadow: 0 14px 26px rgba(97,131,122,0.40), inset 0 1px 0 rgba(255,255,255,0.20);
        }

        .pt-card__action:active { transform: translateY(0); }

        .pt-card__close {
          position: absolute;
          top: 10px;
          right: 10px;
          width: 28px;
          height: 28px;
          border: none;
          border-radius: 999px;
          background: rgba(54,73,91,0.06);
          color: rgba(54,73,91,0.45);
          font-size: 12px;
          cursor: pointer;
          display: grid;
          place-items: center;
          transition: background .18s ease, color .18s ease, transform .18s ease;
        }

        .pt-card__close:hover {
          background: rgba(54,73,91,0.12);
          color: rgba(54,73,91,0.85);
          transform: scale(1.08);
        }

        @media (max-width: 480px) {
          .pt-card { border-radius: 22px; padding: 18px 18px 16px; }
          .pt-card__title { font-size: 15.5px; }
          .pt-card__text { font-size: 13px; }
          .pt-card__icon { font-size: 28px; }
        }

        @media (prefers-reduced-motion: reduce) {
          .pt-spotlight,
          .pt-card__icon,
          .pt-card__pill-dot,
          .pt-scroll-arrow {
            animation: none !important;
          }
        }

        @keyframes ptSpotIn {
          from { opacity: 0; transform: scale(1.08); }
          to   { opacity: 1; transform: scale(1); }
        }

        @keyframes ptSpotPulse {
          0%, 100% {
            box-shadow:
              0 0 0 9999px rgba(15, 23, 42, 0.55),
              inset 0 0 0 1.5px rgba(255, 255, 255, 0.12),
              0 0 0 2px rgba(118, 165, 167, 0.50),
              0 0 28px rgba(118, 165, 167, 0.30);
          }
          50% {
            box-shadow:
              0 0 0 9999px rgba(15, 23, 42, 0.55),
              inset 0 0 0 1.5px rgba(255, 255, 255, 0.18),
              0 0 0 2.5px rgba(118, 165, 167, 0.85),
              0 0 42px rgba(118, 165, 167, 0.55);
          }
        }

        @keyframes ptFadeIn {
          from { opacity: 0; transform: translateX(-50%) translateY(8px); }
          to   { opacity: 1; transform: translateX(-50%) translateY(0); }
        }

        @keyframes ptCardIn {
          from { opacity: 0; transform: translateY(14px) scale(.94); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }

        @keyframes ptBounce {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(6px); }
        }

        @keyframes ptIconBob {
          0%, 100% { transform: translateY(0) scale(1); }
          50%      { transform: translateY(-3px) scale(1.07); }
        }

        @keyframes ptPillPulse {
          0%, 100% { box-shadow: 0 0 0 3px rgba(97,131,122,0.10); }
          50%      { box-shadow: 0 0 0 5px rgba(97,131,122,0.28); }
        }
      `}</style>
    </>
  );
}

export function CountdownOverlay({ isReady = true, onArm, onPrime, onComplete, onSkip, onError, onExited }) {
  const [count, setCount] = useState(3);
  const [phase, setPhase] = useState("loading");
  const [gestureAction, setGestureAction] = useState("arm");
  const busyRef = useRef(false);
  const firedRef = useRef(false);
  const exitTimerRef = useRef(0);
  const handlersRef = useRef({});
  const EXIT_MS = 320;
  const skipButtonRef = useRef(null);

  handlersRef.current = { onArm, onPrime, onComplete, onSkip, onError, onExited };

  useEffect(() => () => window.clearTimeout(exitTimerRef.current), []);

  const runArm = useCallback(async (fromGesture = false) => {
    if (!isReady || busyRef.current || firedRef.current) return false;

    busyRef.current = true;
    setPhase("arming");

    try {
      const { onArm: arm } = handlersRef.current;
      const armed = await Promise.resolve(typeof arm === "function" ? arm({ fromGesture }) : true);

      if (!armed) throw new Error("AUDIO_ARM_FAILED");

      setCount(3);
      setPhase("count");
      return true;
    } catch (error) {
      setGestureAction("arm");
      setPhase("gesture");
      if (fromGesture) handlersRef.current.onError?.(error);
      return false;
    } finally {
      busyRef.current = false;
    }
  }, [isReady]);

  const launch = useCallback(async ({ fromGesture = false, skipped = false } = {}) => {
    if (busyRef.current || firedRef.current) return false;

    busyRef.current = true;
    firedRef.current = true;
    skipButtonRef.current?.blur();
    setPhase("leaving");

    try {
      const { onPrime: prime } = handlersRef.current;
      const primed = await Promise.resolve(typeof prime === "function" ? prime({ fromGesture }) : true);

      if (!primed) throw new Error("AUDIO_PRIME_FAILED");

      const { onComplete: complete, onSkip: skip } = handlersRef.current;
      if (skipped && typeof skip === "function") skip();
      else complete?.();

      exitTimerRef.current = window.setTimeout(() => handlersRef.current.onExited?.(), EXIT_MS);
      return true;
    } catch (error) {
      firedRef.current = false;
      setGestureAction("prime");
      setPhase("gesture");
      if (fromGesture) handlersRef.current.onError?.(error);
      return false;
    } finally {
      busyRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (!isReady || phase !== "loading") return;
    void runArm(false);
  }, [isReady, phase, runArm]);

  useEffect(() => {
    if (phase !== "count") return undefined;

    const timerId = window.setTimeout(() => {
      if (count > 1) setCount((current) => current - 1);
      else void launch();
    }, 1000);

    return () => window.clearTimeout(timerId);
  }, [count, launch, phase]);

  const handleGestureStart = useCallback(() => {
    if (gestureAction === "prime") {
      void launch({ fromGesture: true });
      return;
    }

    void runArm(true);
  }, [gestureAction, launch, runArm]);

  const handleSkip = useCallback(() => {
    if (phase !== "count") return;
    void launch({ fromGesture: true, skipped: true });
  }, [launch, phase]);

  const radius = 52;
  const circumference = +(2 * Math.PI * radius).toFixed(2);
  const hints = ["Installez-vous", "Détendez-vous", "Respirez…"];
  const isCounting = phase === "count";
  const isLeaving = phase === "leaving";
  const showCount = isCounting || isLeaving;
  const isGesture = phase === "gesture";

  return (
    <div className={`cdo-overlay ${isLeaving ? "cdo-overlay--leaving" : ""}`}>
      <div className="cdo-content">
        <p className="cdo-sup">{showCount ? "Préparez-vous" : isGesture ? "Votre session est prête" : "Un instant"}</p>

        <div className="cdo-ring-wrap">
          {showCount ? (
            <>
              <svg className="cdo-ring" viewBox="0 0 128 128" aria-hidden="true">
                <circle cx="64" cy="64" r={radius} className="cdo-ring__bg" />
                <circle key={count} cx="64" cy="64" r={radius} className="cdo-ring__arc" style={{ strokeDasharray: circumference }} />
              </svg>
              <div key={count} className="cdo-num" aria-live="polite">{count}</div>
            </>
          ) : (
            <div className="cdo-prep" aria-hidden="true">
              <img src="/refuge-symbol.svg" alt="" className="cdo-prep__logo" />
            </div>
          )}
        </div>

        <p key={showCount ? count : phase} className="cdo-hint">{showCount ? hints[3 - count] ?? "" : isGesture ? "Touchez pour commencer votre session." : "Préparation de votre session…"}</p>

        {isGesture ? (
          <button type="button" className="cdo-start" onClick={handleGestureStart}>Commencer</button>
        ) : (
<button
  ref={skipButtonRef}
  type="button"
  className={`cdo-skip ${showCount ? "" : "cdo-skip--hidden"}`}
  onClick={handleSkip}
  aria-label="Passer le décompte"
  aria-hidden={!isCounting}
  tabIndex={isCounting ? 0 : -1}
>
  Passer
</button>
        )}
      </div>

      <style>{`
.cdo-overlay {
  position: fixed;
  inset: 0;
  z-index: 1600;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.38);
  backdrop-filter: blur(8px) saturate(108%);
  -webkit-backdrop-filter: blur(8px) saturate(108%);
  animation: cdoFadeIn .3s ease both;
}

.cdo-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;
  padding: 0 24px;
  text-align: center;
}

.cdo-sup {
  margin: 0;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: .12em;
  text-transform: uppercase;
  color: rgba(54, 73, 91, 0.5);
  animation: cdoFadeIn .5s ease both;
}

.cdo-ring-wrap {
  position: relative;
  width: 128px;
  height: 128px;
}

.cdo-ring {
  width: 100%;
  height: 100%;
  transform: rotate(90deg) scaleX(-1);
  transform-origin: center;
}

.cdo-ring__bg {
  fill: none;
  stroke: rgba(54, 73, 91, 0.10);
  stroke-width: 3.5;
}

.cdo-ring__arc {
  fill: none;
  stroke: rgba(97, 131, 122, .85);
  stroke-width: 3.5;
  stroke-linecap: round;
  stroke-dashoffset: 0;
  animation: cdoDrain 1s linear forwards;
  transform-origin: center;
}

.cdo-num {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 54px;
  font-weight: 900;
  color: #36495B;
  letter-spacing: -.02em;
  animation: cdoPop .38s cubic-bezier(.34,1.56,.64,1) both;
}

.cdo-hint {
  margin: 0;
  min-height: 38px;
  max-width: 230px;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.45;
  color: rgba(54, 73, 91, 0.45);
  animation: cdoHint .4s ease both;
}

.cdo-skip {
  margin-top: 4px;
  padding: 6px 14px;
  border: none;
  border-radius: 999px;
  background: transparent;
  color: rgba(54, 73, 91, 0.28);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: .04em;
  cursor: pointer;
  transition: color .15s, background .15s;
}

.cdo-skip:hover {
  background: rgba(54, 73, 91, 0.07);
  color: rgba(54, 73, 91, 0.72);
}

.cdo-start {
  min-width: 154px;
  min-height: 46px;
  padding: 12px 20px;
  border: 0;
  border-radius: 999px;
  background: linear-gradient(135deg, #61837A 0%, #76A5A7 100%);
  color: #ffffff;
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;
  box-shadow: 0 10px 24px rgba(97, 131, 122, 0.28);
}

.cdo-prep {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cdo-prep__logo {
  width: 90px;
  height: auto;
  object-fit: contain;
  animation: cdoLogoFloat 2.6s ease-in-out infinite;
}

.cdo-skip--hidden {
  visibility: hidden;
  pointer-events: none;
}
.cdo-overlay--leaving {
  opacity: 0;
  pointer-events: none;
  animation: cdoFadeOut 320ms ease both;
}

.cdo-overlay--leaving .cdo-content {
  animation: cdoContentOut 320ms ease both;
}

@keyframes cdoFadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
@keyframes cdoFadeOut {
  from { opacity: 1; }
  to { opacity: 0; }
}

@keyframes cdoContentOut {
  from { transform: translateY(0) scale(1); }
  to { transform: translateY(-6px) scale(0.98); }
}
@keyframes cdoDrain {
  from { stroke-dashoffset: 0; }
  to { stroke-dashoffset: ${circumference}; }
}

@keyframes cdoPop {
  from { opacity: 0; transform: scale(.6); }
  to { opacity: 1; transform: scale(1); }
}

@keyframes cdoHint {
  from { opacity: 0; transform: translateY(5px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes cdoLogoFloat {
  0%, 100% { opacity: 0.85; transform: translateY(0) scale(1); }
  50% { opacity: 1; transform: translateY(-7px) scale(1.04); }
}

@media (prefers-reduced-motion: reduce) {
  .cdo-prep__logo { animation: none; }
}
      `}</style>
    </div>
  );
}

function DemoEndOverlay({ listenedSeconds, onSignup, onLogin }) {
  const completed = listenedSeconds >= 47;

  return (
    <div
      className="demo-end-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="demo-end-title"
    >
      <div className="demo-end-card">
        <div
          className="demo-end-logo-wrap"
          aria-hidden="true"
        >
          <img
            className="demo-end-logo"
            src="/refuge-symbol.svg"
            alt=""
          />
        </div>

        <p className="demo-end-eyebrow">
          {completed ? "Votre première respiration" : "Votre découverte Harmonauria"}
        </p>

        <h2
          id="demo-end-title"
          className="demo-end-title"
        >
          Ce calme peut devenir votre Refuge.
        </h2>

        <p className="demo-end-text">
          Créez gratuitement votre espace pour retrouver vos respirations, personnaliser vos ambiances et avancer à votre rythme.
        </p>

        <div
          className="demo-end-benefits"
          aria-label="Ce que vous retrouverez dans votre espace"
        >
          <span>Respirations guidées</span>
          <span>Paysages sonores ajustables</span>
          <span>Progression personnelle</span>
        </div>

        <button
          type="button"
          className="demo-end-primary"
          onClick={onSignup}
        >
          Créer mon espace gratuit
        </button>

        <button
          type="button"
          className="demo-end-login"
          onClick={onLogin}
        >
          J’ai déjà un compte
        </button>

        <p className="demo-end-reassurance">
          Sans carte bancaire · Quelques secondes suffisent
        </p>
      </div>
    </div>
  );
}

function EndSessionOverlay({
  listenedSeconds,
  onSaveNote,
  onFinish,
}) {
  const formatTime = (secs) => {
    const s = Math.round(secs);
    const m = Math.floor(s / 60);
    const rem = s % 60;
    if (m === 0) return `${rem} sec`;
    if (rem === 0) return `${m} min`;
    return `${m} min ${rem} sec`;
  };

  const MESSAGES = [
    {
      title: "Votre séance est terminée",
      text: "Prenez un instant pour observer ce qui s’est apaisé.",
    },
    {
      title: "Un moment pour vous",
      text: "Laissez les effets de la séance s’installer doucement.",
    },
    {
      title: "Le calme peut continuer",
      text: "Gardez avec vous le rythme retrouvé pendant cette séance.",
    },
    {
      title: "Restez ici un instant",
      text: "Respirez naturellement avant de reprendre votre journée.",
    },
  ];

  const [msg] = useState(
    () => MESSAGES[Math.floor(Math.random() * MESSAGES.length)]
  );

  const [noteOpen, setNoteOpen] = useState(false);
const [note, setNote] = useState("");
const [noteSaving, setNoteSaving] = useState(false);
const [noteSaved, setNoteSaved] = useState(false);
const [noteError, setNoteError] = useState("");

const handleSaveNote = async () => {
  const cleanNote = note.trim();

  if (
    !cleanNote ||
    noteSaving ||
    noteSaved ||
    typeof onSaveNote !== "function"
  ) {
    return;
  }

  setNoteSaving(true);
  setNoteError("");

  try {
    await onSaveNote(cleanNote);
    setNoteSaved(true);
    setNoteOpen(false);
  } catch (error) {
    console.error("Erreur sauvegarde note :", error);
    setNoteError(
      "La note n’a pas pu être enregistrée.",
    );
  } finally {
    setNoteSaving(false);
  }
};

  return (
    <div className="eso-overlay">
      <div className="eso-card">
        <p className="eso-sup">Session terminée</p>

        <h2 className="eso-title">{msg.title}</h2>

        <p className="eso-message">{msg.text}</p>

        {listenedSeconds > 8 && (
          <div className="eso-duration">
            <span className="eso-duration-label">Durée de la session</span>
            <span className="eso-duration-value">{formatTime(listenedSeconds)}</span>
          </div>
        )}

<div className="eso-note">
  {noteSaved ? (
    <div
      className="eso-note-saved"
      role="status"
    >
      ✓ Note ajoutée à votre journal
    </div>
  ) : (
    <>
      <button
        type="button"
        className="eso-note-toggle"
        onClick={() =>
          setNoteOpen((current) => !current)
        }
        aria-expanded={noteOpen}
      >
        {noteOpen
          ? "Refermer"
          : "Ajouter quelques mots à mon journal"}
      </button>

      {noteOpen && (
        <div className="eso-note-box">
          <label htmlFor="eso-note-text">
            Comment vous sentez-vous après cette
            séance&nbsp;?
          </label>

          <textarea
            id="eso-note-text"
            value={note}
            maxLength={500}
            rows={3}
            placeholder="Une sensation, une pensée, quelques mots…"
            onChange={(event) => {
              setNote(event.target.value);
              setNoteError("");
            }}
          />

          <button
            type="button"
            className="eso-note-save"
            disabled={!note.trim() || noteSaving}
            onClick={handleSaveNote}
          >
            {noteSaving
              ? "Enregistrement…"
              : "Enregistrer"}
          </button>

          {noteError && (
            <p
              className="eso-note-error"
              role="alert"
            >
              {noteError}
            </p>
          )}
        </div>
      )}
    </>
  )}
</div>

        <button className="eso-btn" onClick={onFinish}>
          Retour à l’accueil
        </button>
      </div>

      <style>{`
.eso-overlay {
  position: fixed;
  inset: 0;
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: transparent;
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
  animation: esoFadeIn .6s ease both;
}

        .eso-card {
          position: relative;
          width: min(390px, 100%);
          max-height: calc(100dvh - 48px);
          overflow-x: hidden;
          overflow-y: auto;

          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 18px;

          padding: 38px 30px 30px;
          text-align: center;

          background: rgba(251, 250, 247, 0.97);
          border: 1px solid rgba(54, 73, 91, 0.10);
          border-radius: 26px;

          box-shadow:
            0 24px 60px rgba(15, 23, 42, 0.10),
            inset 0 1px 0 rgba(255, 255, 255, 0.88);

          animation: esoCardIn 0.5s cubic-bezier(0.22, 1, 0.36, 1) both;
        }

        .eso-sup {
          margin: 0 0 2px;
          color: #9a762e;
          font-size: 11px;
          font-weight: 900;
          line-height: 1;
          letter-spacing: 0.14em;
          text-transform: uppercase;
        }

        .eso-title {
          margin: 0;
          font-family: "Nunito", system-ui, sans-serif;
          font-size: clamp(25px, 6vw, 32px);
          font-weight: 900;
          line-height: 1.12;
          letter-spacing: -0.035em;
          color: #303744;
          text-wrap: balance;
        }

        .eso-message {
          max-width: 310px;
          margin: -6px 0 4px;
          color: #6b7280;
          font-size: 14.5px;
          font-weight: 500;
          line-height: 1.6;
          text-wrap: balance;
        }

        .eso-duration {
          width: 100%;
          padding: 14px 20px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 3px;
          border: 1px solid rgba(54, 73, 91, 0.08);
          border-radius: 14px;
          background: rgba(54, 73, 91, 0.035);
        }

        .eso-duration-label {
          color: #9ca3af;
          font-size: 10.5px;
          font-weight: 800;
          letter-spacing: 0.09em;
          text-transform: uppercase;
        }

        .eso-duration-value {
          color: #303744;
          font-family: "Nunito", system-ui, sans-serif;
          font-size: 25px;
          font-weight: 900;
          letter-spacing: -0.025em;
        }

.eso-btn {
  width: 100%;
  min-height: 54px;
  padding: 15px 20px;
  border: none;
  border-radius: 15px;
  background: linear-gradient(135deg, #76a5a7 0%, #61837a 100%);
  color: #ffffff;
  font-family: var(--font-sans);
  font-size: 15px;
  font-weight: 800;
  cursor: pointer;
  box-shadow: 0 12px 26px rgba(97, 131, 122, 0.28), inset 0 1px 0 rgba(255, 255, 255, 0.22);
  transition: transform 180ms ease, box-shadow 180ms ease, filter 180ms ease;
}

.eso-btn:hover {
  transform: translateY(-2px);
  filter: brightness(1.04);
  box-shadow: 0 16px 32px rgba(97, 131, 122, 0.36), inset 0 1px 0 rgba(255, 255, 255, 0.26);
}

.eso-btn:active {
  transform: translateY(0);
}

        /* Animations */
        @keyframes esoFadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }

        @keyframes esoCardIn {
          from { opacity: 0; transform: translateY(16px) scale(.96); }
          to   { opacity: 1; transform: translateY(0)   scale(1);    }
        }

        @keyframes esoBreath {
          0%, 100% { transform: scale(1);    box-shadow: 0 8px 24px rgba(118,165,167,.25); }
          50%       { transform: scale(1.06); box-shadow: 0 12px 32px rgba(118,165,167,.38); }
        }

        @media (prefers-reduced-motion: reduce) {
          .eso-overlay, .eso-card {
            animation: none !important;
          }
        }
      `}</style>
    </div>
  );
}

export function FullscreenBreathing({
  technique,
  isPlaying,
  currentTime,
  audioRef,
  sound,
  onExit
}) {
  return (
    <>
      <div
        className="fsb-overlay"
        onClick={onExit}
        onKeyDown={(e) => {
          if (e.key === "Escape" || e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onExit();
          }
        }}
        role="button"
        tabIndex={0}
        aria-label="Retour au lecteur"
      >
        <div className="fsb-handle-zone">
          <div className="fsb-handle" />
        </div>

        {/* IMPORTANT : plus de stopPropagation => clic n'importe où = sortie */}
        <div className="fsb-body">
<BreathingAnimation
  technique={technique}
  isPlaying={isPlaying}
  currentTime={currentTime}
  audioRef={audioRef}
  large={true}
/>

          {sound?.name && (
            <p className="fsb-sound-name">{sound.name}</p>
          )}
        </div>

        <p className="fsb-hint">Appuyez n’importe où pour revenir</p>
      </div>

      <style>{`
        .fsb-overlay {
          position: fixed;
          inset: 0;
          z-index: 10050;
          display: flex;
          flex-direction: column;
          align-items: center;
          background: rgba(251, 250, 247, 0.66);
          backdrop-filter: blur(16px) saturate(105%);
          -webkit-backdrop-filter: blur(16px) saturate(105%);
          cursor: pointer;
          animation: fsbFadeIn .35s ease both;
          padding:
            max(env(safe-area-inset-top, 0px), 20px)
            env(safe-area-inset-right, 0px)
            max(env(safe-area-inset-bottom, 0px), 24px)
            env(safe-area-inset-left, 0px);
          box-sizing: border-box;
          outline: none;
        }

        .fsb-handle-zone {
          width: 100%;
          display: flex;
          justify-content: center;
          padding: 8px 0 24px;
          flex-shrink: 0;
        }

        .fsb-handle {
          width: 38px;
          height: 4px;
          border-radius: 999px;
          background: rgba(54, 73, 91, 0.14);
          transition: background .2s ease;
        }

        .fsb-overlay:hover .fsb-handle,
        .fsb-overlay:focus-visible .fsb-handle {
          background: rgba(54, 73, 91, 0.28);
        }

        .fsb-body {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 44px;
          width: 100%;
        }

.fsb-sound-name {
  margin: -32px 0 0;
  transform: translateY(-18px);

  font-family: "Nunito", system-ui, sans-serif;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.20em;
  text-transform: uppercase;
  color: rgba(54, 73, 91, 0.42);
  text-align: center;
  animation: fsbFadeIn .5s .25s ease both;
}

.fsb-hint {
  margin: 0;
  font-family: "DM Sans", system-ui, sans-serif;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #36495B;
  text-align: center;
  flex-shrink: 0;
  padding: 10px 14px;
  margin-bottom: 8px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.82);
  border: 1px solid rgba(54, 73, 91, 0.10);
  box-shadow:
    0 8px 24px rgba(15, 23, 42, 0.08),
    0 1px 0 rgba(255, 255, 255, 0.9) inset;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  animation: fsbPulse 3s 1s ease-in-out infinite both;
}

.fsb-overlay .breathing-animation-compact--large {
  /* Une SEULE variable → width et height identiques garantis (pas de déformation)
     On divise par 1.45 pour absorber le scale max (~1.4) sans déborder */
--breath-size: min(
  calc((100vw - 32px) / 1.2),
  calc((100dvh - 230px) / 1.2),
  500px
);

  flex: 1;
  width: 100%;
  height: auto;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: visible;
}

.fsb-overlay .breathing-animation-compact--large .breathing-shape {
  width: var(--breath-size);
  height: var(--breath-size);
  max-width: none;
  max-height: none;
}

/* Texte lisible à grande taille */
.fsb-overlay .breathing-animation-compact--large .breathing-label-main {

  letter-spacing: 0.10em;
  line-height: 1.2;
}

.fsb-overlay .breathing-animation-compact--large .breathing-label-main--physio {

  line-height: 1.2;
}

.fsb-overlay .breathing-animation-compact--large
  .breathing-label-inside--triangle .breathing-label-main {

  letter-spacing: 0.16em;
}

@media (max-width: 480px) {
  .fsb-overlay .breathing-animation-compact--large {
    --breath-size: min(
  calc((100vw - 24px) / 1.2),
  calc((100dvh - 200px) / 1.2),
  380px
    );
  }
}

        @keyframes fsbFadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }

        @keyframes fsbPulse {
          0%, 100% { opacity: 0.16; }
          50%      { opacity: 0.36; }
        }

@media (max-width: 480px) {
  .fsb-body { gap: 32px; }

.fsb-sound-name {
  margin-top: -42px;
  transform: translateY(-22px);
  font-size: 11px;
  letter-spacing: 0.16em;
}

  .fsb-hint {
    font-size: 11px;
    padding: 10px 12px;
    max-width: calc(100vw - 32px);
  }
}

        @media (prefers-reduced-motion: reduce) {
          .fsb-overlay { animation: none; }
          .fsb-sound-name { animation: none; }
          .fsb-hint { animation: none; opacity: 0.28; }
        }
      `}</style>
    </>
  );
}

export default function PlayerPage({
  user,
  sound,
  withMusic,
  mainSrc,
  breathingSrc,
  selectedDuration,
  isPremium,
  longestStreak = 0,
  naturalSounds,
  onThemeToggle,
  onOpenPremium,
  onMusicModeChange,
  onBack,
  onFinish,
  onSaveNote,
  onFinalize,
  lastBadge,
  resetLastBadge,
demoMode = false,
initialNaturalSoundId = null,
initialNaturalSoundVolume = 0.7,
onDemoSignup,
onDemoLogin,
}) {
  // ========== STATES ==========
  const [isLoadingAudio, setIsLoadingAudio] = useState(false);
  const [audioError, setAudioError] = useState(null);
  const [isAudioReady, setIsAudioReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isSwitchingMode, setIsSwitchingMode] = useState(false);
  const [listeningWithMusic, setListeningWithMusic] = useState(withMusic !== false);

  const playerHistoryIndexRef = useRef(
    typeof window !== "undefined" &&
      Number.isFinite(window.history.state?.idx)
      ? window.history.state.idx
      : null,
  );

  const availableListeningSources = useMemo(() => {
    const files =
      sound?.files?.[selectedDuration] ||
      sound?.files?.["5min"] ||
      {};

    return {
      music: files.music || "",
      noMusic: files.noMusic || "",
    };
  }, [sound, selectedDuration]);

  useEffect(() => {
    setListeningWithMusic(withMusic !== false);
  }, [withMusic, mainSrc, sound?.id, selectedDuration]);

  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
const [, setTrackDuration] = useState(0);

  const [isBreathingMuted, setIsBreathingMuted] = useState(false);

const [baseVolume, setBaseVolume] = useState(() => {
  try {
    const v = parseFloat(localStorage.getItem(getBaseVolumeKey(user?.uid)));
    return Number.isFinite(v) && v >= 0 && v <= 1 ? v : 1;
  } catch {
    return 1;
  }
});
const baseVolumeLiveRef = useRef(baseVolume);

// Recharge le volume quand l'uid change (changement de compte)
useEffect(() => {
  let nextVolume = 1;

  try {
    const storedVolume = parseFloat(localStorage.getItem(getBaseVolumeKey(user?.uid)));
    nextVolume = Number.isFinite(storedVolume) && storedVolume >= 0 && storedVolume <= 1 ? storedVolume : 1;
  } catch {}

  baseVolumeLiveRef.current = nextVolume;
  setBaseVolume(nextVolume);
}, [user?.uid]);

// Sauvegarde à chaque changement de volume
useEffect(() => {
  try {
    localStorage.setItem(getBaseVolumeKey(user?.uid), String(baseVolume));
  } catch {}
}, [baseVolume, user?.uid]);

  const [showBaseVolume, setShowBaseVolume] = useState(false);

  const [selectedNaturalSounds, setSelectedNaturalSounds] = useState([]);
  const [naturalSoundTouched, setNaturalSoundTouched] = useState(false);

const [isCountingDown, setIsCountingDown] = useState(true);

  const [sessionEnded, setSessionEnded] = useState(false);

  const [isBreathingFullscreen, setIsBreathingFullscreen] = useState(false);

  const [naturalLoading, setNaturalLoading] = useState({}); // id -> bool
  const setNaturalLoadingFlag = useCallback((id, v) => {
  setNaturalLoading((prev) => ({ ...prev, [id]: v }));
  }, []);

  // ========== REFS ==========
  const selectedNaturalSetRef = useRef(new Set());
  const naturalOpTokenRef = useRef({});
  const naturalVolumesRef = useRef({});

  const audioRef = useRef(null);
  const alternateAudioRef = useRef(null);
  const activeMainRef = useRef(null);
  const breathingRef = useRef(null);
  const naturalRefs = useRef({});
  const naturalAbortRef = useRef({});
  const initialAmbiancesAppliedRef = useRef(false);
  const naturalPreloadPromiseRef = useRef({});
  const baseVolumeBtnRef = useRef(null);
  const baseVolumePopoverRef = useRef(null);
  const transportOpRef = useRef(0);
  const transportShouldPlayRef = useRef(false);
  const transportArmedRef = useRef(false);
  const startupPreparingRef = useRef(false);
  const breathingStartEnabledRef = useRef(Boolean(breathingSrc));
  const bufferingRef = useRef(false);
  const bufferingTimerRef = useRef(null);
  const finalFadeEndAtRef = useRef(0);
  const finalFadeTimeoutRef = useRef(null);
  const modeSwitchTokenRef = useRef(0);
  const isSwitchingModeRef = useRef(false);
  const playerScreenRef = useRef(null);
  const autoFullscreenTimerRef = useRef(null);
  const autoFullscreenUsedRef = useRef(false);
  const wakeLockRef = useRef(null);
  const postStartSyncTimeoutsRef = useRef([]);
const currentTimeRef = useRef(0);  
const rafFrameRef = useRef(0);     
const pendingTimeoutsRef = useRef(new Set());
const onFinalizeRef = useRef(onFinalize);

useEffect(() => {
  onFinalizeRef.current = onFinalize;
}, [onFinalize]);

// Sortie non gérée (déconnexion, changement de route) : l'écoute réelle
// est enregistrée avant que le player disparaisse.
useEffect(() => {
  return () => {
    finalizeSession("unmount", currentTimeRef.current);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

const getMainDecks = useCallback(
  () => [audioRef.current, alternateAudioRef.current].filter(Boolean),
  [],
);

const getActiveMainAudio = useCallback(() => {
  return activeMainRef.current || audioRef.current;
}, []);

const getInactiveMainAudio = useCallback(() => {
  const active = getActiveMainAudio();

  return active === audioRef.current
    ? alternateAudioRef.current
    : audioRef.current;
}, [getActiveMainAudio]);

const getMainAudioId = useCallback((element) => {
  return element === alternateAudioRef.current ? "main-b" : "main-a";
}, []);

const pauseAllMainDecks = useCallback(
  ({ resetTo = null } = {}) => {
    getMainDecks().forEach((deck) => {
      try {
        deck.pause();
        if (resetTo !== null) deck.currentTime = resetTo;
      } catch {}
    });
  },
  [getMainDecks],
);

const trackedTimeout = useCallback((fn, ms) => {
  const id = setTimeout(() => {
    pendingTimeoutsRef.current.delete(id);
    fn();
  }, ms);

  pendingTimeoutsRef.current.add(id);
  return id;
}, []);

const requestWakeLock = useCallback(async () => {
  if (!('wakeLock' in navigator)) return;
  try {
    if (wakeLockRef.current) return; // déjà actif
    wakeLockRef.current = await navigator.wakeLock.request('screen');
  } catch {}
}, []);

const releaseWakeLock = useCallback(async () => {
  if (wakeLockRef.current) {
    try { await wakeLockRef.current.release(); } catch {}
    wakeLockRef.current = null;
  }
}, []);

const clearAutoFullscreenTimer = useCallback(() => {
  if (autoFullscreenTimerRef.current) {
    clearTimeout(autoFullscreenTimerRef.current);
    autoFullscreenTimerRef.current = null;
  }
}, []);

const openBreathingFullscreen = useCallback(() => {
  if (!isAudioReady || sessionEnded || audioError) return;

  // Une ouverture manuelle ou auto compte comme "déjà utilisé" pour la session
  autoFullscreenUsedRef.current = true;
  clearAutoFullscreenTimer();
  setIsBreathingFullscreen(true);
}, [isAudioReady, sessionEnded, audioError, clearAutoFullscreenTimer]);

const armAutoFullscreenTimer = useCallback(() => {
  clearAutoFullscreenTimer();
if (demoMode) return;
  if (
    autoFullscreenUsedRef.current ||
    !isAudioReady ||
    isCountingDown ||
    sessionEnded ||
    !!audioError ||
    !isPlaying ||
    isBreathingFullscreen
  ) {
    return;
  }

  autoFullscreenTimerRef.current = window.setTimeout(() => {
    if (
      autoFullscreenUsedRef.current ||
      !isAudioReady ||
      isCountingDown ||
      sessionEnded ||
      !!audioError ||
      !isPlaying ||
      isBreathingFullscreen
    ) {
      return;
    }

    openBreathingFullscreen();
  }, AUTO_FULLSCREEN_IDLE_MS);
}, [
  clearAutoFullscreenTimer,
  demoMode,
  isAudioReady,
  isCountingDown,
  sessionEnded,
  audioError,
  isPlaying,
  isBreathingFullscreen,
  openBreathingFullscreen,
]);

useLayoutEffect(() => {
  autoFullscreenUsedRef.current = false;
  countedListenRef.current = false;
  clearAutoFullscreenTimer();

  currentTimeRef.current = 0;
  rafFrameRef.current = 0;
  transportShouldPlayRef.current = false;
  transportArmedRef.current = false;
  startupPreparingRef.current = false;
  breathingStartEnabledRef.current = Boolean(breathingSrc);
  bufferingRef.current = false;
setIsCountingDown(true);
  setIsBreathingFullscreen(false);
  setSessionEnded(false);
}, [mainSrc, breathingSrc, clearAutoFullscreenTimer]);

  const sessionLimit =
  selectedDuration === "5min"
    ? null
    : (SESSION_LIMIT_SECONDS[selectedDuration] ?? null);
  const limitReachedRef = useRef(false);

  const fadeOutStartedRef = useRef(false);
  const countedListenRef = useRef(false);

  const DEFAULT_ARTWORK_URL = useMemo(
    () => new URL("/refuge-symbol.svg", window.location.origin).href,
    []
  );

/* Toute écoute réelle compte, dès la première seconde. Le verrou empêche
   seulement qu'une même séance soit enregistrée deux fois. */
const finalizeSession = useCallback((reason, time) => {
  const seconds = Math.max(0, Number(time) || 0);
  if (seconds <= 0 || countedListenRef.current) return;
  countedListenRef.current = true;
  onFinalizeRef.current?.(reason, seconds);
}, []);

const preloadOneNatural = useCallback((ns) => {
    const id = ns.id;
    if (naturalPreloadPromiseRef.current[id]) return naturalPreloadPromiseRef.current[id];
    const ctrl = new AbortController();
    naturalAbortRef.current[id] = ctrl;
    const element = naturalRefs.current[id];
    const promise = (async () => {
      try {
        const source = await resolveAudioSource(ns.file);
        if (ctrl.signal.aborted || !element || naturalRefs.current[id] !== element) return false;
        const absoluteSource = new URL(source, window.location.origin).href;
        if (!element.paused || selectedNaturalSetRef.current.has(id)) return true;
        element.crossOrigin = "anonymous";
        element.preload = "auto";
        if (element.src !== absoluteSource) { element.src = source; element.load(); }
        await waitUntilPlayable(element, 4000);
        return !ctrl.signal.aborted;
      } catch { return false; }
      finally {
        if (naturalAbortRef.current[id] === ctrl) {
          delete naturalAbortRef.current[id];
          delete naturalPreloadPromiseRef.current[id];
        }
      }
    })();
    naturalPreloadPromiseRef.current[id] = promise;
    return promise;
  }, []);

useEffect(() => {
  if (isCountingDown || !isAudioReady || !Array.isArray(naturalSounds) || naturalSounds.length === 0) return undefined;

  const list = naturalSounds.filter((ns) => (!ns.premium || isPremium) && ns.id !== initialNaturalSoundId);
  let cancelled = false;
  let index = 0;

  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  const concurrency = connection?.saveData ? 1 : 3;

  const run = async () => {
    const workers = Array.from({ length: concurrency }, async () => {
      while (!cancelled && index < list.length) {
        const ns = list[index++];
        await preloadOneNatural(ns);
      }
    });

    await Promise.all(workers);
  };

  void run();

  return () => {
    cancelled = true;
  };
}, [initialNaturalSoundId, isAudioReady, isCountingDown, isPremium, naturalSounds, preloadOneNatural]);

const clearPostStartSyncs = useCallback(() => {
  postStartSyncTimeoutsRef.current.forEach((id) => window.clearTimeout(id));
  postStartSyncTimeoutsRef.current = [];
}, []);

const syncBreathingToMain = useCallback((force = false) => {
  const a = getActiveMainAudio();
  const b = breathingRef.current;

  if (!force || !a || !b?.src || !b.paused || b.seeking || b.readyState < 1) return;

  const duration = Number(b.duration);
  const elapsed = Math.max(0, Number(a.currentTime) || 0);
  const mainTime = b.loop && Number.isFinite(duration) && duration > 0 ? elapsed % duration : elapsed;
  const breathTime = b.currentTime || 0;
  const drift = Math.abs(breathTime - mainTime);

  if (drift > FORCED_SYNC_TOLERANCE) {
    try {
      b.currentTime = mainTime;
    } catch {}
  }
}, [getActiveMainAudio]);

const schedulePostStartBreathingSync = useCallback(() => {
  clearPostStartSyncs();

  requestAnimationFrame(() => syncBreathingToMain(true));

  postStartSyncTimeoutsRef.current = [
    window.setTimeout(() => syncBreathingToMain(true), 120),
    window.setTimeout(() => syncBreathingToMain(true), 320),
  ];
}, [clearPostStartSyncs, syncBreathingToMain]);

const fadeOutAllActiveAudio = useCallback((fadeMs = FINAL_FADE_MS) => {
  const safeFadeMs = Math.max(180, Math.min(FINAL_FADE_MS, Number(fadeMs) || FINAL_FADE_MS));

  const fadeElement = (element, id) => {
    if (!element?.src || element.paused) return;

    audioManager.ensureElement(element, id);

    if (typeof audioManager.fadeOut === "function") {
      audioManager.fadeOut(id, safeFadeMs);
    } else {
      audioManager.setVolume(id, 0, safeFadeMs);
    }
  };

  getMainDecks().forEach((deck) => {
    fadeElement(deck, getMainAudioId(deck));
  });

  fadeElement(breathingRef.current, "breathing");

  const naturalIds = new Set([
    ...selectedNaturalSetRef.current,
    ...Object.keys(naturalRefs.current).filter((soundId) => {
      const element = naturalRefs.current[soundId];
      return Boolean(element?.src && !element.paused);
    }),
  ]);

  naturalIds.forEach((soundId) => {
    naturalOpTokenRef.current[soundId] = (naturalOpTokenRef.current[soundId] || 0) + 1;
    fadeElement(naturalRefs.current[soundId], `natural-${soundId}`);
  });
}, [getMainAudioId, getMainDecks]);

const fadeAllForPause = useCallback((fadeMs = PAUSE_FADE_MS) => {
  const fadeElement = (element, id) => {
    if (!element?.src || element.paused) return;
    audioManager.ensureElement(element, id);
    audioManager.setVolume(id, 0, fadeMs);
  };

  getMainDecks().forEach((deck) => fadeElement(deck, getMainAudioId(deck)));
  fadeElement(breathingRef.current, "breathing");
  selectedNaturalSetRef.current.forEach((soundId) => fadeElement(naturalRefs.current[soundId], `natural-${soundId}`));
}, [getMainAudioId, getMainDecks]);

const getPreparedSupportLayers = useCallback(() => {
  const layers = [];
  const breathing = breathingRef.current;

  if (breathingStartEnabledRef.current && breathing?.src) {
    layers.push({ element: breathing, id: "breathing", kind: "breathing", soundId: null });
  }

  selectedNaturalSetRef.current.forEach((soundId) => {
    const element = naturalRefs.current[soundId];
    if (element?.src && element.readyState >= 3) layers.push({ element, id: `natural-${soundId}`, kind: "natural", soundId });
  });

  return layers;
}, []);

const removeFailedPreparedLayers = useCallback((layers, results = [], requirePlaying = false) => {
  const failedNaturalIds = new Set();

  layers.forEach((layer, index) => {
    if (results[index] && (!requirePlaying || !layer.element.paused)) return;

    audioManager.setVolume(layer.id, 0, 0);
    pauseAndResetMedia([layer.element], 0);

    if (layer.kind === "breathing") {
      breathingStartEnabledRef.current = false;
      setIsBreathingMuted(true);
      return;
    }

    if (layer.soundId) failedNaturalIds.add(layer.soundId);
  });

  if (failedNaturalIds.size === 0) return;

  selectedNaturalSetRef.current = new Set([...selectedNaturalSetRef.current].filter((soundId) => !failedNaturalIds.has(soundId)));
  setSelectedNaturalSounds([...selectedNaturalSetRef.current]);
}, []);

const armPreparedPlayback = useCallback(async () => {
  const active = getActiveMainAudio();

  if (!active?.src || !isAudioReady || sessionEnded || audioError) return false;
  if (transportArmedRef.current) return true;

  const layers = getPreparedSupportLayers();
  const elements = [active, ...layers.map((layer) => layer.element)];
  const mainId = getMainAudioId(active);

  startupPreparingRef.current = true;
  transportShouldPlayRef.current = false;
  pauseAndResetMedia(elements, 0);
  audioManager.ensureElement(active, mainId);
  audioManager.setVolume(mainId, 0, 0);
  active.muted = false;

  layers.forEach((layer) => {
    audioManager.ensureElement(layer.element, layer.id);
    audioManager.setVolume(layer.id, 0, 0);
    layer.element.muted = false;
  });

  let unlockRequest;

  try {
    unlockRequest = audioManager.unlock();
  } catch {
    unlockRequest = Promise.resolve();
  }

  const armRequest = armMediaGroup(active, layers.map((layer) => layer.element), (element) => audioManager.play(element));
  const [, result] = await Promise.all([Promise.resolve(unlockRequest).catch(() => {}), armRequest]);

  removeFailedPreparedLayers(layers, result.optionalResults, false);
  startupPreparingRef.current = false;

  if (!result.mainArmed) {
    pauseAndResetMedia(elements, 0);
    return false;
  }

  transportArmedRef.current = true;
  return true;
}, [audioError, getActiveMainAudio, getMainAudioId, getPreparedSupportLayers, isAudioReady, removeFailedPreparedLayers, sessionEnded]);

const primePreparedPlayback = useCallback(async () => {
  if (!transportArmedRef.current) {
    const armed = await armPreparedPlayback();
    if (!armed) return false;
  }

  const active = getActiveMainAudio();
  if (!active?.src) return false;

  const layers = getPreparedSupportLayers();
  const elements = [active, ...layers.map((layer) => layer.element)];
  const mainId = getMainAudioId(active);

  startupPreparingRef.current = true;
  transportShouldPlayRef.current = true;
  bufferingRef.current = false;
  pauseAndResetMedia(elements, 0);
  audioManager.ensureElement(active, mainId);
  audioManager.setVolume(mainId, 0, 0);

  layers.forEach((layer) => {
    audioManager.ensureElement(layer.element, layer.id);
    audioManager.setVolume(layer.id, 0, 0);
  });

  let unlockRequest;

  try {
    unlockRequest = audioManager.unlock();
  } catch {
    unlockRequest = Promise.resolve();
  }

  const startRequest = startMediaGroup(active, layers.map((layer) => layer.element), (element) => audioManager.play(element));
  const [, result] = await Promise.all([Promise.resolve(unlockRequest).catch(() => {}), startRequest]);

  removeFailedPreparedLayers(layers, result.optionalResults, true);

  if (!result.mainStarted || active.paused) {
    startupPreparingRef.current = false;
    transportShouldPlayRef.current = false;
    pauseAndResetMedia(elements, 0);
    return false;
  }

  return true;
}, [armPreparedPlayback, getActiveMainAudio, getMainAudioId, getPreparedSupportLayers, removeFailedPreparedLayers]);

const revealPreparedPlayback = useCallback(() => {
  const active = getActiveMainAudio();

  if (!transportArmedRef.current || !active?.src || active.paused) return false;

  const mainId = getMainAudioId(active);
  const targetVolume = baseVolumeLiveRef.current;
  if ((Number(active.currentTime) || 0) > REVEAL_REANCHOR_TOLERANCE) {
    try { active.currentTime = 0; } catch {}
  }
  startupPreparingRef.current = false;
  transportArmedRef.current = false;
  transportShouldPlayRef.current = true;
  bufferingRef.current = false;
  audioManager.setVolume(mainId, targetVolume, START_FADE_MS);

  getPreparedSupportLayers().forEach((layer) => {
    if (layer.element.paused) return;

    if (layer.kind === "breathing") {
      audioManager.setVolume(layer.id, isBreathingMuted ? 0 : targetVolume * BREATH_VOL, BREATHING_START_FADE_MS);
      return;
    }

    const volume = naturalVolumesRef.current[layer.soundId] ?? 0.7;
    audioManager.setVolume(layer.id, volume * NATURAL_GAIN, START_FADE_MS);
  });

  const startedAt = active.currentTime || 0;
  currentTimeRef.current = startedAt;
  setCurrentTime(startedAt);
  setIsPlaying(true);
  syncBreathingToMain(true);
  schedulePostStartBreathingSync();
  requestWakeLock();

  if (!rafRef.current) rafRef.current = requestAnimationFrame(() => updateProgressRef.current?.());
  return true;
}, [getActiveMainAudio, getMainAudioId, getPreparedSupportLayers, isBreathingMuted, requestWakeLock, schedulePostStartBreathingSync, syncBreathingToMain]);

const failPreparedPlayback = useCallback(() => {
  startupPreparingRef.current = false;
  transportArmedRef.current = false;
  transportShouldPlayRef.current = false;
  bufferingRef.current = false;
  pauseAndResetMedia([...getMainDecks(), breathingRef.current, ...Object.values(naturalRefs.current)], 0);
  setIsCountingDown(false);
  setIsPlaying(false);
  setAudioError("La lecture audio n’est pas disponible sur cet appareil.");
}, [getMainDecks]);

const startTransportPlayback = useCallback(async () => {
  const active = getActiveMainAudio();

  if (!active?.src || !isAudioReady) return false;

  const mainAudioId = getMainAudioId(active);
  const targetVolume = baseVolumeLiveRef.current;
  const layers = getPreparedSupportLayers();

  audioManager.ensureElement(active, mainAudioId);
  audioManager.setVolume(mainAudioId, 0, 0);

  layers.forEach((layer) => {
    audioManager.ensureElement(layer.element, layer.id);
    audioManager.setVolume(layer.id, 0, 0);

    if (layer.kind === "breathing") {
      try {
        layer.element.currentTime = active.currentTime || 0;
      } catch {}
    }
  });

  transportShouldPlayRef.current = true;
  bufferingRef.current = false;

  let unlockRequest;

  try {
    unlockRequest = audioManager.unlock();
  } catch {
    unlockRequest = Promise.resolve();
  }

  const startRequest = startMediaGroup(active, layers.map((layer) => layer.element), (element) => audioManager.play(element));
  const [, result] = await Promise.all([Promise.resolve(unlockRequest).catch(() => {}), startRequest]);

  removeFailedPreparedLayers(layers, result.optionalResults, true);

  if (!result.mainStarted || active.paused) {
    transportShouldPlayRef.current = false;
    bufferingRef.current = false;
    pauseAndResetMedia([active, ...layers.map((layer) => layer.element)], null);
    setIsPlaying(false);
    releaseWakeLock();
    return false;
  }

  audioManager.setVolume(mainAudioId, targetVolume, RESUME_FADE_MS);

  layers.forEach((layer) => {
    if (layer.element.paused) return;

    if (layer.kind === "breathing") {
      audioManager.setVolume(layer.id, isBreathingMuted ? 0 : targetVolume * BREATH_VOL, RESUME_FADE_MS);
      return;
    }

    const volume = naturalVolumesRef.current[layer.soundId] ?? 0.7;
    audioManager.setVolume(layer.id, volume * NATURAL_GAIN, RESUME_FADE_MS);
  });

  syncBreathingToMain(true);
  schedulePostStartBreathingSync();
  setIsPlaying(true);
  requestWakeLock();
  return true;
}, [
  getActiveMainAudio,
  getMainAudioId,
  getPreparedSupportLayers,
  isAudioReady,
  isBreathingMuted,
  releaseWakeLock,
  removeFailedPreparedLayers,
  requestWakeLock,
  schedulePostStartBreathingSync,
  syncBreathingToMain,
]);

  // ========== SYNC SELECTED NATURALS ==========
  useEffect(() => {
    selectedNaturalSetRef.current = new Set(selectedNaturalSounds);
  }, [selectedNaturalSounds]);

  useEffect(() => {
  setNaturalSoundTouched(false);
}, [mainSrc]);

  // ========== AUDIO LOADING & AUTOPLAY ==========
useEffect(() => {
  const primary = audioRef.current;
  const alternate = alternateAudioRef.current;
  const breathing = breathingRef.current;

  if (!primary || !alternate || !mainSrc) return;

  let cancelled = false;
  activeMainRef.current = primary;

  const prepareDeck = async (deck, id, src, volume, required = true) => {
    try {
      deck.pause();
      deck.currentTime = 0;
    } catch {}

    if (!src) {
      try {
        deck.removeAttribute("src");
        deck.load();
      } catch {}
      return !required;
    }

    deck.crossOrigin = "anonymous";
    deck.preload = "auto";
    const resolvedSrc = await resolveAudioSource(src);
    if (cancelled) return false;
    deck.src = resolvedSrc;

    try {
      deck.load();
    } catch {}

    if (required) {
      await waitUntilPlayable(deck, 12000);
    } else {
      void waitUntilPlayable(deck, 12000).catch(() => {
        console.warn("Préchargement de la piste alternative différé.");
      });
    }

    if (cancelled) return false;

    audioManager.setupElement(deck, id);
    audioManager.setVolume(id, volume, 0);
    return true;
  };

  const boot = async () => {
    try {
      setAudioError(null);
      setIsLoadingAudio(true);
      setIsAudioReady(false);
      setIsPlaying(false);
      setCurrentTime(0);
      setDuration(0);
      setTrackDuration(0);
      setIsBreathingMuted(false);
      setShowBaseVolume(false);
selectedNaturalSetRef.current = new Set();
initialAmbiancesAppliedRef.current = false;
naturalVolumesRef.current = {};
setSelectedNaturalSounds([]);
      currentTimeRef.current = 0;
      transportShouldPlayRef.current = false;
      bufferingRef.current = false;
      limitReachedRef.current = false;
      fadeOutStartedRef.current = false;
      finalFadeEndAtRef.current = 0;
      if (finalFadeTimeoutRef.current) {
        window.clearTimeout(finalFadeTimeoutRef.current);
        finalFadeTimeoutRef.current = null;
      }
      setIsCountingDown(true);
      setSessionEnded(false);
      clearPostStartSyncs();

      pauseAllMainDecks({ resetTo: 0 });
      try {
        if (breathing) {
          breathing.pause();
          breathing.currentTime = 0;
        }
      } catch {}

      try {
        void Promise.resolve(audioManager.unlock()).catch(() => {});
      } catch {}

      if (cancelled) return;

      const alternateSrc =
        mainSrc === availableListeningSources.music
          ? availableListeningSources.noMusic
          : availableListeningSources.music;

      const mainPreparation = prepareDeck(primary, "main-a", mainSrc, 0, true);
      void prepareDeck(alternate, "main-b", alternateSrc, 0, false).catch((error) => { if (!cancelled) console.warn("Piste alternative différée.", error); });
      const breathingPreparation = (async () => {
      if (breathing) {
        breathingStartEnabledRef.current = Boolean(breathingSrc);

        if (!breathingSrc) {
          try {
            breathing.pause();
            breathing.removeAttribute("src");
            breathing.load();
          } catch {}
        } else {
          breathing.crossOrigin = "anonymous";
          breathing.preload = "auto";
          const resolvedBreathingSource = await resolveAudioSource(breathingSrc);
          if (cancelled) return;
          breathing.src = resolvedBreathingSource;

          try {
            breathing.load();
            await waitUntilPlayable(breathing, 12000);

            if (cancelled) return;

            audioManager.setupElement(breathing, "breathing");
            audioManager.setVolume("breathing", 0, 0);
            breathing.currentTime = 0;
          } catch (error) {
            console.warn("Le guide respiratoire audio est indisponible, la session continue.", error);
            breathingStartEnabledRef.current = false;
            setIsBreathingMuted(true);

            try {
              breathing.pause();
              breathing.removeAttribute("src");
              breathing.load();
            } catch {}
          }
        }
      }

      })();
      await Promise.all([mainPreparation, breathingPreparation]);
      if (cancelled) return;
const initialNatural = initialNaturalSoundId ? naturalSounds.find((sound) => sound.id === initialNaturalSoundId) : null;

if (initialNatural && (!initialNatural.premium || isPremium)) void preloadOneNatural(initialNatural);
      setIsAudioReady(true);
    } catch (error) {
      if (cancelled) return;

      console.error("❌ Audio loading error:", error);
      setAudioError(error?.message || "Erreur de chargement");
      setIsAudioReady(false);
      setIsPlaying(false);
    } finally {
      if (!cancelled) setIsLoadingAudio(false);
    }
  };

  void boot();

  return () => {
    cancelled = true;
  };
}, [
  mainSrc,
  breathingSrc,
  availableListeningSources.music,
  availableListeningSources.noMusic,
  clearPostStartSyncs,
  pauseAllMainDecks,
  initialNaturalSoundId,
  initialNaturalSoundVolume,
  isPremium,
  naturalSounds,
  preloadOneNatural,
]);


  const stopAllNaturalSounds = useCallback(() => {
  Object.keys(naturalRefs.current).forEach((id) => {
    naturalOpTokenRef.current[id] = (naturalOpTokenRef.current[id] || 0) + 1;
    
    audioManager.setVolume(`natural-${id}`, 0, 80);
    
    setTimeout(() => {
      const el = naturalRefs.current[id];
      if (el) {
        try {
          el.pause();
          el.currentTime = 0;
        } catch {}
      }
      audioManager.cleanup(`natural-${id}`);
    }, 120);
  });
  
  selectedNaturalSetRef.current = new Set();
  setSelectedNaturalSounds([]);
}, []);

  const rafRef = useRef(null);
  const updateProgressRef = useRef(null);

  const updateProgress = useCallback(() => {
    const activeMain = getActiveMainAudio();
    const breathing = breathingRef.current;

    if (!activeMain) return;

    const t = activeMain.currentTime || 0;

    if (breathing?.src && !breathing.paused) {
      syncBreathingToMain(false);
    }

    const realDuration = Number.isFinite(activeMain.duration)
      ? activeMain.duration
      : 0;

    const endAt = sessionLimit
      ? Math.min(sessionLimit, realDuration || sessionLimit)
      : realDuration;

    if (endAt > 0) {
      const remaining = endAt - t;

      if (!fadeOutStartedRef.current && remaining > 0 && remaining <= FINAL_FADE_LEAD_SECONDS) {
        const fadeMs = Math.max(180, Math.min(FINAL_FADE_MS, remaining * 1000));

        fadeOutStartedRef.current = true;
        finalFadeEndAtRef.current = performance.now() + fadeMs;
        fadeOutAllActiveAudio(fadeMs);
      }
    }

if (
  sessionLimit &&
  !limitReachedRef.current &&
  t >= sessionLimit - LIMIT_EPS
) {
  const fadeStillRunning =
    fadeOutStartedRef.current &&
    performance.now() < finalFadeEndAtRef.current;

  if (fadeStillRunning) return;

  limitReachedRef.current = true;
      transportShouldPlayRef.current = false;
      bufferingRef.current = false;
      clearPostStartSyncs();

      pauseAllMainDecks({ resetTo: sessionLimit });

      if (breathing) {
        try {
          breathing.pause();
          breathing.currentTime = sessionLimit;
        } catch {}
      }

      stopAllNaturalSounds();
      setCurrentTime(sessionLimit);
      currentTimeRef.current = sessionLimit;
      setIsPlaying(false);
      finalizeSession("cutoff", sessionLimit);
      setSessionEnded(true);
      return;
    }

    const display = sessionLimit ? Math.min(t, sessionLimit) : t;
    currentTimeRef.current = display;

    if (++rafFrameRef.current % 2 === 0) {
      setCurrentTime(display);
    }

  }, [
    getActiveMainAudio,
    sessionLimit,
    clearPostStartSyncs,
    pauseAllMainDecks,
    stopAllNaturalSounds,
    fadeOutAllActiveAudio,
    finalizeSession,
    syncBreathingToMain,
  ]);

useEffect(() => {
  updateProgressRef.current = updateProgress;
}, [updateProgress]);


  // ========== RAF DE PROGRESSION UNIQUE ==========
  useEffect(() => {
    if (!isPlaying) {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }

      return undefined;
    }

    const frame = () => {
      updateProgressRef.current?.();

      if (transportShouldPlayRef.current && !sessionEnded) {
        rafRef.current = requestAnimationFrame(frame);
        return;
      }

      rafRef.current = null;
    };

    rafRef.current = requestAnimationFrame(frame);

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [isPlaying, sessionEnded]);

  // ========== AUDIO EVENTS DES DEUX DECKS ==========
useEffect(() => {
  const decks = getMainDecks();
  if (decks.length === 0) return undefined;

  const isActiveDeck = (element) => element === getActiveMainAudio();
  const clearBufferingTimer = () => {
    if (!bufferingTimerRef.current) return;
    window.clearTimeout(bufferingTimerRef.current);
    bufferingTimerRef.current = null;
  };
  const pauseSupportLayers = () => {
    try {
      breathingRef.current?.pause();
    } catch {}
    selectedNaturalSetRef.current.forEach((soundId) => {
      try {
        naturalRefs.current[soundId]?.pause();
      } catch {}
    });
  };
  const resumeSupportLayers = async (activeDeck) => {
    const layers = getPreparedSupportLayers();
    const targetVolume = baseVolumeLiveRef.current;

    layers.forEach((layer) => {
      audioManager.ensureElement(layer.element, layer.id);

      if (layer.kind === "breathing") {
        try {
          layer.element.currentTime = activeDeck.currentTime || 0;
        } catch {}
        audioManager.setVolume(layer.id, isBreathingMuted ? 0 : targetVolume * BREATH_VOL, 0);
        return;
      }

      const volume = naturalVolumesRef.current[layer.soundId] ?? 0.7;
      audioManager.setVolume(layer.id, volume * NATURAL_GAIN, 0);
    });

    const result = await startMediaGroup(activeDeck, layers.map((layer) => layer.element), (element) => audioManager.play(element));
    removeFailedPreparedLayers(layers, result.optionalResults, true);
    syncBreathingToMain(true);
    return Boolean(result.mainStarted && !activeDeck.paused);
  };
  const finishEndedSession = (deck) => {
    finalizeSession("ended", deck.currentTime || currentTimeRef.current || 0);
    setIsPlaying(false);
    releaseWakeLock();

    const breathing = breathingRef.current;
    if (breathing) {
      try {
        breathing.pause();
        breathing.currentTime = 0;
      } catch {}
    }

    stopAllNaturalSounds();
    setSessionEnded(true);
  };
  const onMeta = (event) => {
    const deck = event.currentTarget;
    if (!isActiveDeck(deck)) return;
    const full = Number.isFinite(deck.duration) ? deck.duration : 0;
    setTrackDuration(full);
    setDuration(sessionLimit ? Math.min(full, sessionLimit) : full);
  };
  const onTimeUpdate = (event) => {
  if (startupPreparingRef.current || !isActiveDeck(event.currentTarget)) return;
  updateProgressRef.current?.();
};
  const onEnded = (event) => {
    const deck = event.currentTarget;
    if (!isActiveDeck(deck)) return;

    transportShouldPlayRef.current = false;
    bufferingRef.current = false;
    clearBufferingTimer();
    clearPostStartSyncs();

    if (!fadeOutStartedRef.current) {
      const fallbackFadeMs = 500;

      fadeOutStartedRef.current = true;
      finalFadeEndAtRef.current = performance.now() + fallbackFadeMs;
      fadeOutAllActiveAudio(fallbackFadeMs);
    }

    const remainingFadeMs = Math.max(0, finalFadeEndAtRef.current - performance.now());
    if (finalFadeTimeoutRef.current) window.clearTimeout(finalFadeTimeoutRef.current);

    if (remainingFadeMs > 40) {
      finalFadeTimeoutRef.current = window.setTimeout(() => {
        finalFadeTimeoutRef.current = null;
        finishEndedSession(deck);
      }, remainingFadeMs + 20);
    } else {
      finishEndedSession(deck);
    }
  };
  const onBuffering = (event) => {
    if (startupPreparingRef.current) return;
    const deck = event.currentTarget;
    if (!isActiveDeck(deck) || !transportShouldPlayRef.current || isSwitchingModeRef.current) return;

    clearBufferingTimer();
    bufferingTimerRef.current = window.setTimeout(() => {
      bufferingTimerRef.current = null;
      if (!isActiveDeck(deck) || !transportShouldPlayRef.current || deck.paused || deck.readyState >= 3) return;
      bufferingRef.current = true;
      setIsPlaying(false);
      pauseSupportLayers();
    }, 180);
  };
  const onPlaying = (event) => {
    if (startupPreparingRef.current) return;
    const deck = event.currentTarget;
    if (!isActiveDeck(deck) || !transportShouldPlayRef.current) return;

    clearBufferingTimer();
    const wasBuffering = bufferingRef.current;
    bufferingRef.current = false;

    if (wasBuffering) {
      void resumeSupportLayers(deck).finally(() => {
        if (transportShouldPlayRef.current && !deck.paused) {
          setIsPlaying(true);
          requestWakeLock();
        }
      });
      return;
    }

    setIsPlaying(true);
    requestWakeLock();
  };
  const onPause = (event) => {
    if (startupPreparingRef.current || !isActiveDeck(event.currentTarget) || isSwitchingModeRef.current) return;
    clearBufferingTimer();
    setIsPlaying(false);
    releaseWakeLock();
  };
  const onBeforeUnload = () => {
    const active = getActiveMainAudio();
    const time = active?.currentTime || currentTimeRef.current || 0;
    finalizeSession("beforeunload", time);
  };

  decks.forEach((deck) => {
    deck.addEventListener("loadedmetadata", onMeta);
    deck.addEventListener("ended", onEnded);
    deck.addEventListener("waiting", onBuffering);
    deck.addEventListener("playing", onPlaying);
    deck.addEventListener("pause", onPause);
    deck.addEventListener("emptied", onPause);
    deck.addEventListener("timeupdate", onTimeUpdate);
  });
  window.addEventListener("beforeunload", onBeforeUnload);

  return () => {
    clearBufferingTimer();
    decks.forEach((deck) => {
      deck.removeEventListener("loadedmetadata", onMeta);
      deck.removeEventListener("ended", onEnded);
      deck.removeEventListener("waiting", onBuffering);
      deck.removeEventListener("playing", onPlaying);
      deck.removeEventListener("pause", onPause);
      deck.removeEventListener("emptied", onPause);
      deck.removeEventListener("timeupdate", onTimeUpdate);
    });
    window.removeEventListener("beforeunload", onBeforeUnload);
  };
}, [clearPostStartSyncs, fadeOutAllActiveAudio, finalizeSession, getActiveMainAudio, getMainDecks, getPreparedSupportLayers, isBreathingMuted, releaseWakeLock, removeFailedPreparedLayers, requestWakeLock, sessionLimit, stopAllNaturalSounds, syncBreathingToMain]);
  // ========== BASE VOLUME POPOVER CLOSE ==========
  useEffect(() => {
    if (!showBaseVolume) return;

    const onPointerDown = (e) => {
      const pop = baseVolumePopoverRef.current;
      const btn = baseVolumeBtnRef.current;

      if (pop?.contains(e.target) || btn?.contains(e.target)) return;

      setShowBaseVolume(false);
    };

    const onKeyDown = (e) => {
      if (e.key === "Escape") setShowBaseVolume(false);
    };

    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [showBaseVolume]);

  // ========== VISIBILITY CHANGE ==========
useEffect(() => {
  const onVisibilityChange = async () => {
    if (document.visibilityState !== "visible") {
      wakeLockRef.current = null;
      return;
    }

    await audioManager.unlock();

    if (fadeOutStartedRef.current) return;

    audioManager.resyncVolume("main-a");
    audioManager.resyncVolume("main-b");
    audioManager.resyncVolume("breathing");
    selectedNaturalSetRef.current.forEach((soundId) => audioManager.resyncVolume(`natural-${soundId}`));

    const active = getActiveMainAudio();
    if (active && active.paused && !active.ended && transportShouldPlayRef.current) {
      try {
        await new Promise((resolve) => window.setTimeout(resolve, 200));
        const ok = await startTransportPlayback("resume");
        if (ok) {
          bufferingRef.current = false;
          setIsPlaying(true);
        }
      } catch {}
    }

    if (active && !active.paused) requestWakeLock();
  };

  document.addEventListener("visibilitychange", onVisibilityChange);
  return () => document.removeEventListener("visibilitychange", onVisibilityChange);
}, [getActiveMainAudio, requestWakeLock, startTransportPlayback]);

const togglePlayPause = useCallback(async () => {
  if (isTransitioning || isSwitchingModeRef.current) return;

  const active = getActiveMainAudio();
  const breathing = breathingRef.current;

  if (!active || !active.src || !isAudioReady || isCountingDown) return;

  setIsTransitioning(true);

  try {
    const operation = ++transportOpRef.current;

    if (!active.paused) {
      transportShouldPlayRef.current = false;
      bufferingRef.current = false;
      clearPostStartSyncs();

      if (fadeOutStartedRef.current) {
        fadeOutStartedRef.current = false;
        finalFadeEndAtRef.current = 0;

        if (finalFadeTimeoutRef.current) {
          window.clearTimeout(finalFadeTimeoutRef.current);
          finalFadeTimeoutRef.current = null;
        }
      }

      fadeAllForPause();
      setIsPlaying(false);
      releaseWakeLock();

      await new Promise((resolve) => trackedTimeout(resolve, PAUSE_FADE_MS));

      pauseAllMainDecks();

      try {
        breathing?.pause();
      } catch {}

      selectedNaturalSetRef.current.forEach((soundId) => {
        try {
          naturalRefs.current[soundId]?.pause();
        } catch {}
      });

      return;
    }

    const realDuration = Number.isFinite(active.duration)
      ? active.duration
      : 0;

    const endAt = sessionLimit
      ? Math.min(realDuration || sessionLimit, sessionLimit)
      : realDuration;

    if (
      active.ended ||
      (endAt > 0 && active.currentTime >= endAt - LIMIT_EPS)
    ) {
      pauseAllMainDecks({ resetTo: 0 });

      if (breathing?.src) {
        try {
          breathing.currentTime = 0;
        } catch {}
      }

      currentTimeRef.current = 0;
      setCurrentTime(0);
      limitReachedRef.current = false;
      fadeOutStartedRef.current = false;
      finalFadeEndAtRef.current = 0;
      if (finalFadeTimeoutRef.current) {
        window.clearTimeout(finalFadeTimeoutRef.current);
        finalFadeTimeoutRef.current = null;
      }
    }

    const ok = await startTransportPlayback("resume");

    if (transportOpRef.current !== operation) return;
    if (!ok) {
      transportShouldPlayRef.current = false;
      setAudioError("Impossible de reprendre la lecture audio.");
    }
  } finally {
    setIsTransitioning(false);
  }
}, [
  getActiveMainAudio,
  isAudioReady,
  isCountingDown,
  sessionLimit,
  clearPostStartSyncs,
  fadeAllForPause,
  pauseAllMainDecks,
  releaseWakeLock,
  startTransportPlayback,
  trackedTimeout,
  isTransitioning,
]);

const handleBaseVolumeInput = useCallback((event) => {
  const nextVolume = Math.max(0, Math.min(1, Number(event.currentTarget.value) || 0));

  baseVolumeLiveRef.current = nextVolume;

  if (fadeOutStartedRef.current) return;

  const active = getActiveMainAudio();

  getMainDecks().forEach((deck) => {
    if (!deck?.src || (deck.paused && deck !== active)) return;

    const id = getMainAudioId(deck);
    audioManager.ensureElement(deck, id);
    audioManager.setLiveVolume(id, nextVolume);
  });

  const breathing = breathingRef.current;

  if (breathing?.src) {
    audioManager.ensureElement(breathing, "breathing");
    audioManager.setLiveVolume("breathing", isBreathingMuted ? 0 : nextVolume * BREATH_VOL);
  }
}, [getActiveMainAudio, getMainAudioId, getMainDecks, isBreathingMuted]);

const commitBaseVolume = useCallback(() => {
  const nextVolume = baseVolumeLiveRef.current;

  setBaseVolume((currentVolume) => currentVolume === nextVolume ? currentVolume : nextVolume);
}, []);

const toggleBreathingMute = useCallback(async () => {
  if (fadeOutStartedRef.current || sessionEnded) return;

  const breathing = breathingRef.current;
  const active = getActiveMainAudio();
  if (!breathing) return;

  const nextMuted = !isBreathingMuted;
  setIsBreathingMuted(nextMuted);

  if (breathing.src) {
    audioManager.ensureElement(breathing, "breathing");
  }

  if (!nextMuted && active && !active.paused && breathing.paused && breathing.src) {
    try {
      breathing.currentTime = active.currentTime || 0;
    } catch {}

    await audioManager.play(breathing);
    syncBreathingToMain(true);
    schedulePostStartBreathingSync();
  }

  if (breathing.src) {
    audioManager.setLiveVolume("breathing", nextMuted ? 0 : baseVolumeLiveRef.current * BREATH_VOL);
  }
}, [getActiveMainAudio, isBreathingMuted, schedulePostStartBreathingSync, sessionEnded, syncBreathingToMain]);


const toggleNaturalSound = useCallback(
  async (soundId, volume = 0.7) => {
    if (fadeOutStartedRef.current || sessionEnded) return false;

    const ns = naturalSounds.find((sound) => sound.id === soundId);
    if (!ns) return false;

    if (ns.premium && !isPremium) {
      onOpenPremium?.();
      return false;
    }

    const el = naturalRefs.current[soundId];
    if (!el) return false;

    const token = (naturalOpTokenRef.current[soundId] = (naturalOpTokenRef.current[soundId] || 0) + 1);
    const isOnNow = selectedNaturalSetRef.current.has(soundId);

    if (isOnNow) {
      selectedNaturalSetRef.current.delete(soundId);
      setSelectedNaturalSounds((prev) => prev.filter((id) => id !== soundId));
      setNaturalLoadingFlag(soundId, false);
      audioManager.setVolume(`natural-${soundId}`, 0, 220);

      trackedTimeout(() => {
        if (naturalOpTokenRef.current[soundId] !== token) return;
        try {
          el.pause();
          el.currentTime = 0;
        } catch {}
      }, 260);

      return true;
    }

    selectedNaturalSetRef.current.add(soundId);
    setSelectedNaturalSounds((prev) => prev.includes(soundId) ? prev : [...prev, soundId]);
    naturalVolumesRef.current[soundId] = volume;
    setNaturalLoadingFlag(soundId, true);

    try {
let sourceChanged = false;
let nextSource = "";

if (!nextSource) {
  naturalAbortRef.current[soundId]?.abort();
  nextSource = await resolveAudioSource(ns.file);
}

if (naturalOpTokenRef.current[soundId] !== token) return false;
if (naturalRefs.current[soundId] !== el || !transportShouldPlayRef.current) return false;

el.crossOrigin = "anonymous";

      if (!nextSource) throw new Error("natural source missing");

      const absoluteNextSource = nextSource.startsWith("blob:") ? nextSource : new URL(nextSource, window.location.origin).href;
      if (el.src !== absoluteNextSource) {
        el.src = nextSource;
        sourceChanged = true;
      }

      el.preload = "auto";
      el.loop = true;
      el.muted = false;
      audioManager.ensureElement(el, `natural-${soundId}`);
      audioManager.setVolume(`natural-${soundId}`, 0, 0);

      if (sourceChanged || el.readyState === 0) {
        try {
          el.load();
        } catch {}
      }

if (el.readyState < 3) await waitUntilPlayable(el, 8000);
if (naturalOpTokenRef.current[soundId] !== token) return false;
if (naturalRefs.current[soundId] !== el || !transportShouldPlayRef.current) return false;
const ok = await audioManager.play(el);

      if (naturalOpTokenRef.current[soundId] !== token) return false;
      if (!transportShouldPlayRef.current || naturalRefs.current[soundId] !== el) { try { el.pause(); } catch {} return false; }
      if (!ok || el.paused) throw new Error("Play failed (natural)");

      audioManager.setVolume(`natural-${soundId}`, volume * NATURAL_GAIN, AMBIANCE_START_FADE_MS);
      return true;
    } catch (error) {
      if (naturalOpTokenRef.current[soundId] !== token || naturalRefs.current[soundId] !== el) return false;
      selectedNaturalSetRef.current.delete(soundId);
      setSelectedNaturalSounds((prev) => prev.filter((id) => id !== soundId));
      audioManager.setVolume(`natural-${soundId}`, 0, 0);
      try {
        el.pause();
        el.currentTime = 0;
      } catch {}
      console.warn("❌ Natural sound start failed:", error);
      return false;
} finally {
  if (naturalOpTokenRef.current[soundId] === token && naturalRefs.current[soundId] === el) setNaturalLoadingFlag(soundId, false);
}
  },
  [isPremium, naturalSounds, onOpenPremium, sessionEnded, setNaturalLoadingFlag, trackedTimeout]
);

useEffect(() => {
  if (!isPlaying || isCountingDown || initialAmbiancesAppliedRef.current) return;
  initialAmbiancesAppliedRef.current = true;
  if (!initialNaturalSoundId || selectedNaturalSetRef.current.has(initialNaturalSoundId)) return;
  void toggleNaturalSound(initialNaturalSoundId, Math.max(0, Math.min(1, Number(initialNaturalSoundVolume) || 0)));
}, [isPlaying, isCountingDown, initialNaturalSoundId, initialNaturalSoundVolume, toggleNaturalSound]);

const changeNaturalVolume = useCallback((soundId, volume) => {
  const safeVolume = Math.max(0, Math.min(1, Number(volume) || 0));

  naturalVolumesRef.current[soundId] = safeVolume;

  if (fadeOutStartedRef.current) return;

  const element = naturalRefs.current[soundId];

  if (element) {
    audioManager.ensureElement(element, `natural-${soundId}`);
  }

  audioManager.setLiveVolume(`natural-${soundId}`, safeVolume * NATURAL_GAIN);
}, []);

const switchListeningMode = useCallback(
  async (nextWithMusic) => {
    const outgoing = getActiveMainAudio();
    const incoming = getInactiveMainAudio();
    const nextSrc = nextWithMusic ? availableListeningSources.music : availableListeningSources.noMusic;

    if (!outgoing || !incoming || !nextSrc || nextWithMusic === listeningWithMusic || isSwitchingModeRef.current || !isAudioReady || isCountingDown || sessionEnded || audioError || fadeOutStartedRef.current) return;

    const token = ++modeSwitchTokenRef.current;
    const outgoingId = getMainAudioId(outgoing);
    const incomingId = getMainAudioId(incoming);
    const wasPlaying = !outgoing.paused;

    isSwitchingModeRef.current = true;
    setIsSwitchingMode(true);
    setAudioError(null);
    transportOpRef.current += 1;
    clearPostStartSyncs();

    const readMasterTime = () => Number.isFinite(outgoing.currentTime) ? outgoing.currentTime : currentTimeRef.current || 0;
    const alignIncoming = () => {
      const targetTime = readMasterTime();
      const maxTime = Number.isFinite(incoming.duration) && incoming.duration > 0 ? Math.max(0, incoming.duration - 0.12) : targetTime;
      const safeTime = Math.max(0, Math.min(targetTime, maxTime));
      if (Math.abs((incoming.currentTime || 0) - safeTime) > MODE_SYNC_TOLERANCE) {
        try {
          incoming.currentTime = safeTime;
        } catch {}
      }
      return safeTime;
    };

    try {
      const resolvedNextSrc = await resolveAudioSource(nextSrc);
      if (modeSwitchTokenRef.current !== token) return;
      const absoluteNextSrc = new URL(resolvedNextSrc, window.location.origin).href;
      const incomingSrc = incoming.currentSrc || incoming.src;

      if (incomingSrc !== absoluteNextSrc) {
        try {
          incoming.pause();
        } catch {}
        incoming.crossOrigin = "anonymous";
        incoming.preload = "auto";
        incoming.src = resolvedNextSrc;
        try {
          incoming.load();
        } catch {}
      }

      await waitUntilPlayable(incoming, 12000);
      if (modeSwitchTokenRef.current !== token) return;

      audioManager.ensureElement(outgoing, outgoingId);
      audioManager.ensureElement(incoming, incomingId);
      audioManager.setVolume(incomingId, 0, 0);
      incoming.muted = false;
      alignIncoming();

      if (wasPlaying) {
        const started = await audioManager.play(incoming);
        if (modeSwitchTokenRef.current !== token) { audioManager.cancelPendingPlay(incoming); incoming.pause(); return; }
        if (!started || incoming.paused) throw new Error("La nouvelle piste n'a pas pu démarrer.");

        await new Promise((resolve) => requestAnimationFrame(resolve));
        alignIncoming();

        audioManager.setVolume(outgoingId, 0, MODE_CROSSFADE_MS);
        audioManager.setVolume(incomingId, baseVolumeLiveRef.current, MODE_CROSSFADE_MS);
        await new Promise((resolve) => window.setTimeout(resolve, MODE_CROSSFADE_MS + 30));
        if (modeSwitchTokenRef.current !== token) { incoming.pause(); return; }
      } else {
        audioManager.setVolume(incomingId, baseVolumeLiveRef.current, 0);
      }

      if (modeSwitchTokenRef.current !== token) return;

      const finalSynchronizedTime = alignIncoming();
      activeMainRef.current = incoming;
      currentTimeRef.current = finalSynchronizedTime;
      setCurrentTime(finalSynchronizedTime);

      if (Number.isFinite(incoming.duration) && incoming.duration > 0) {
        setTrackDuration(incoming.duration);
        setDuration(sessionLimit ? Math.min(incoming.duration, sessionLimit) : incoming.duration);
      }

      try {
        outgoing.pause();
      } catch {}
      audioManager.setVolume(outgoingId, 0, 0);

      setListeningWithMusic(nextWithMusic);
      onMusicModeChange?.(nextWithMusic);
    } catch (error) {
      console.error("Erreur transition d'ambiance :", error);
      try {
        incoming.pause();
      } catch {}
      audioManager.setVolume(incomingId, 0, 0);
      activeMainRef.current = outgoing;

      if (wasPlaying) {
        audioManager.ensureElement(outgoing, outgoingId);
        if (outgoing.paused) await audioManager.play(outgoing);
        audioManager.setVolume(outgoingId, baseVolumeLiveRef.current, 180);
        setIsPlaying(true);
      }
    } finally {
      if (modeSwitchTokenRef.current === token) {
        isSwitchingModeRef.current = false;
        setIsSwitchingMode(false);
      }
    }
  },
  [
    getActiveMainAudio,
    getInactiveMainAudio,
    getMainAudioId,
    availableListeningSources.music,
    availableListeningSources.noMusic,
    listeningWithMusic,
    isAudioReady,
    isCountingDown,
    sessionEnded,
    audioError,
    sessionLimit,
    clearPostStartSyncs,
    onMusicModeChange,
  ],
);


const handleBack = useCallback(() => {
  transportShouldPlayRef.current = false;
  transportArmedRef.current = false;
  startupPreparingRef.current = false;
  bufferingRef.current = false;
  modeSwitchTokenRef.current += 1;
  isSwitchingModeRef.current = false;
  setIsSwitchingMode(false);
  clearAutoFullscreenTimer();
  clearPostStartSyncs();
  setIsBreathingFullscreen(false);

  const active = getActiveMainAudio();
  const time = active?.currentTime || currentTimeRef.current || 0;

  finalizeSession("player-back", time);

  pauseAllMainDecks({ resetTo: 0 });

  try {
    const breathing = breathingRef.current;
    breathing?.pause();
    if (breathing) breathing.currentTime = 0;
  } catch {}

  stopAllNaturalSounds();
  setIsPlaying(false);
  onBack?.();
}, [
  getActiveMainAudio,
  finalizeSession,
  onBack,
  pauseAllMainDecks,
  stopAllNaturalSounds,
  clearAutoFullscreenTimer,
  clearPostStartSyncs,
]);

const handleFinish = useCallback(() => {
  transportShouldPlayRef.current = false;
  transportArmedRef.current = false;
  startupPreparingRef.current = false;
  bufferingRef.current = false;
  modeSwitchTokenRef.current += 1;
  isSwitchingModeRef.current = false;
  setIsSwitchingMode(false);
  clearAutoFullscreenTimer();
  clearPostStartSyncs();
  setIsBreathingFullscreen(false);

  const active = getActiveMainAudio();
  const time = active?.currentTime || currentTimeRef.current || 0;

  if (demoMode) {
    transportOpRef.current += 1;
    fadeOutStartedRef.current = true;
    setIsCountingDown(false);
    setAudioError(null);
    setCurrentTime(time);
    currentTimeRef.current = time;
    setIsPlaying(false);
    setSessionEnded(true);
    releaseWakeLock();
    fadeOutAllActiveAudio(320);

    trackedTimeout(() => {
      pauseAllMainDecks({
        resetTo: 0,
      });

      try {
        const breathing = breathingRef.current;
        breathing?.pause();

        if (breathing) {
          breathing.currentTime = 0;
        }
      } catch {
        /* noop */
      }

      stopAllNaturalSounds();
    }, 340);

    return;
  }

  finalizeSession("finish", time);
  pauseAllMainDecks({
    resetTo: 0,
  });

  try {
    const breathing = breathingRef.current;
    breathing?.pause();

    if (breathing) {
      breathing.currentTime = 0;
    }
  } catch {
    /* noop */
  }

  stopAllNaturalSounds();
  setIsPlaying(false);
  onFinish?.();
}, [
  getActiveMainAudio,
  demoMode,
  fadeOutAllActiveAudio,
  finalizeSession,
  onFinish,
  pauseAllMainDecks,
  releaseWakeLock,
  stopAllNaturalSounds,
  trackedTimeout,
  clearAutoFullscreenTimer,
  clearPostStartSyncs,
]);

useEffect(() => {
  const handleBrowserHistoryChange = (event) => {
    const previousIndex = playerHistoryIndexRef.current;
    const nextIndex = Number.isFinite(event.state?.idx)
      ? event.state.idx
      : null;

    playerHistoryIndexRef.current = nextIndex;

    const isForwardNavigation =
      previousIndex !== null &&
      nextIndex !== null &&
      nextIndex >= previousIndex;

    if (isForwardNavigation) return;

    // Le retour navigateur exécute exactement la même sortie
    // que le bouton « Terminer » du lecteur.
    handleFinish();
  };

  window.addEventListener("popstate", handleBrowserHistoryChange);

  return () => {
    window.removeEventListener("popstate", handleBrowserHistoryChange);
  };
}, [handleFinish]);

  // ========== MEDIA SESSION ==========
  useEffect(() => {
    if (!("mediaSession" in navigator)) return;
    if (typeof window.MediaMetadata === "undefined") return;
    if (!sound) return;

    navigator.mediaSession.metadata = new MediaMetadata({
      title: sound?.name || "Session de respiration",
      artist: listeningWithMusic ? "Avec musique" : "Ondes seules",
      album: "Respiration",
      artwork: [{ src: DEFAULT_ARTWORK_URL, sizes: "512x512", type: "image/png" }],
    });

    navigator.mediaSession.playbackState = isPlaying ? "playing" : "paused";
try {
navigator.mediaSession.setActionHandler("play", () => {
  if (getActiveMainAudio()?.paused) togglePlayPause();
});

navigator.mediaSession.setActionHandler("pause", () => {
  if (!getActiveMainAudio()?.paused) togglePlayPause();
});
  // ✅ Ne pas enregistrer "stop" sur iOS : il interrompt la session
  // au lieu de juste stopper → laisser null
  navigator.mediaSession.setActionHandler("stop", null);
} catch (e) {
  // certains navigateurs n'acceptent pas tous les handlers
}
return () => {
  try {
    navigator.mediaSession.setActionHandler("play", null);
    navigator.mediaSession.setActionHandler("pause", null);
    navigator.mediaSession.setActionHandler("stop", null);
  } catch (e) {}
};
}, [
  sound,
  listeningWithMusic,
  isPlaying,
  DEFAULT_ARTWORK_URL,
  getActiveMainAudio,
  togglePlayPause,
  handleFinish,
]);


useEffect(() => {
  const mainDecks = getMainDecks();
  const breathing = breathingRef.current;
  const timeouts = pendingTimeoutsRef.current;
  const naturalElements = naturalRefs.current;
  const naturalOpTokens = naturalOpTokenRef.current;

  return () => {
    transportShouldPlayRef.current = false;
    transportArmedRef.current = false;
    startupPreparingRef.current = false;
    bufferingRef.current = false;
    modeSwitchTokenRef.current += 1;
    isSwitchingModeRef.current = false;

    if (bufferingTimerRef.current) window.clearTimeout(bufferingTimerRef.current);
    if (finalFadeTimeoutRef.current) window.clearTimeout(finalFadeTimeoutRef.current);
    bufferingTimerRef.current = null;
    finalFadeTimeoutRef.current = null;

    Object.keys(naturalOpTokens).forEach((id) => { naturalOpTokens[id] += 1; });
    Object.values(naturalAbortRef.current).forEach((controller) => {
      try {
        controller.abort();
      } catch {}
    });
    naturalAbortRef.current = {};
    naturalPreloadPromiseRef.current = {};

    timeouts.forEach((id) => clearTimeout(id));
    timeouts.clear();

    clearPostStartSyncs();
    releaseWakeLock();
    audioManager.cleanup("main-a", { hard: true });
    audioManager.cleanup("main-b", { hard: true });
    audioManager.cleanup("breathing", { hard: true });
Object.keys(naturalElements).forEach((soundId) => {
  audioManager.cleanup(`natural-${soundId}`, { hard: true });
});

    mainDecks.forEach((deck) => {
      try {
        deck.pause();
      } catch {}
    });

    try {
      breathing?.pause();
    } catch {}
  };
}, [getMainDecks, releaseWakeLock, clearPostStartSyncs]);

useEffect(() => {
  const root = playerScreenRef.current;

  if (!root) {
    clearAutoFullscreenTimer();
    return;
  }

  if (
    autoFullscreenUsedRef.current ||
    !isAudioReady ||
    isCountingDown ||
    sessionEnded ||
    !!audioError ||
    !isPlaying ||
    isBreathingFullscreen
  ) {
    clearAutoFullscreenTimer();
    return;
  }

  const onActivity = () => {
    armAutoFullscreenTimer();
  };

  // On arme une première fois dès que les conditions sont réunies
  armAutoFullscreenTimer();

  root.addEventListener("pointerdown", onActivity, { passive: true });
  root.addEventListener("touchstart", onActivity, { passive: true });
  root.addEventListener("click", onActivity, { passive: true });
  root.addEventListener("wheel", onActivity, { passive: true });

  const onKeyDown = () => {
    armAutoFullscreenTimer();
  };

  document.addEventListener("keydown", onKeyDown);

  return () => {
    clearAutoFullscreenTimer();
    root.removeEventListener("pointerdown", onActivity);
    root.removeEventListener("touchstart", onActivity);
    root.removeEventListener("click", onActivity);
    root.removeEventListener("wheel", onActivity);
    document.removeEventListener("keydown", onKeyDown);
  };
}, [
  isAudioReady,
  isCountingDown,
  sessionEnded,
  audioError,
  isPlaying,
  isBreathingFullscreen,
  armAutoFullscreenTimer,
  clearAutoFullscreenTimer,
]);
const replayPlayerTour = !demoMode && user?.guidesSeen?.playerTour === false;
const canShowPlayerTooltips =
  !demoMode &&
  isAudioReady &&
  !isCountingDown &&
  !sessionEnded &&
  !audioError &&
  !isBreathingFullscreen;

  // ========== RENDER ==========
  return (
<>
  <audio ref={audioRef} preload="auto" playsInline style={{ display: "none" }} />
  <audio
    ref={alternateAudioRef}
    preload="auto"
    playsInline
    style={{ display: "none" }}
  />
  <audio ref={breathingRef} preload="auto" playsInline style={{ display: "none" }} />

<div
  ref={playerScreenRef}
  className={demoMode ? "public-demo-player" : ""}
>
  {demoMode ? (
<button
  type="button"
  className="public-demo-finish"
  onClick={handleFinish}
  aria-label="Terminer la démonstration"
>
  <svg
    className="public-demo-finish__icon"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M7 7l10 10M17 7L7 17"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>

  <span>Terminer</span>
</button>
  ) : (
    <TopbarInner
      title="Terminer"
      onBack={handleFinish}
      onTitleClick={handleFinish}
      onLogoClick={handleFinish}
      onThemeToggle={onThemeToggle}
      isPremium={isPremium}
      longestStreak={longestStreak}
      titleColor="rgba(54, 73, 91, 0.78)"
    />
  )}

  <div className={`player-container app-fill app-safe--no-top player--full ${demoMode ? "player--demo" : "player-with-topbar"}`}>
        <div className="player-card player-card--flat">
          {(isLoadingAudio || !isAudioReady || audioError) && (
            <div className="loading-overlay">
              {audioError ? (
                <>
                  <div className="loading-error">⚠️</div>
                  <p className="loading-text">{audioError}</p>
                  <button onClick={handleBack} className="btn-primary btn-sm">
                    Retour
                  </button>
                </>
              ) : (
                <>
                  <div className="loading-spinner"></div>
                  <p className="loading-text">
                    {isLoadingAudio ? "Chargement..." : "Préparation de l'audio..."}
                  </p>
                </>
              )}
            </div>
          )}

<div className="player-info">
  <p className="player-info__eyebrow">
    Respiration guidée
  </p>

  <h2 className="player-title">
    {sound?.name}
  </h2>

  <p className="player-description">
    {sound?.description}
  </p>
</div>

<div className="player-breathing-zone">
  <Tooltip
    id="breathing-fullscreen-v1"
    text="Touchez la respiration pour l’afficher en grand."
    placement="bottom"
    priority={50}
    showDelay={1000}
    enabled={canShowPlayerTooltips}
    sequence="player-guidance"
    order={1}
    maxWidth={280}
  >
    <button
      type="button"
      onClick={() => openBreathingFullscreen()}
      className="breathing-fullscreen-trigger"
      aria-label="Ouvrir l’animation en grand écran"
      disabled={!isAudioReady || sessionEnded}
      title="Passer en plein écran"
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 0,
        margin: 0,
        border: "none",
        background: "transparent",
        cursor: isAudioReady && !sessionEnded ? "pointer" : "default",
        WebkitTapHighlightColor: "transparent",
      }}
    >
      <BreathingAnimation
        key={mainSrc}
        technique={sound?.id}
        isPlaying={isPlaying}
        currentTime={currentTime}
        audioRef={breathingRef}
      />
    </button>
  </Tooltip>
</div>



<div className="player-transport">
<div className="progress-container">
  <input
    type="range"
    min={0}
    max={duration || 0}
    step={0.01}
    value={currentTime}
    readOnly
    tabIndex={-1}
    className="progress-bar progress-bar--readonly"
    aria-label="Progression de lecture"
    aria-readonly="true"
    onChange={() => {}}
    onPointerDown={(e) => e.preventDefault()}
    onMouseDown={(e) => e.preventDefault()}
    onTouchStart={(e) => e.preventDefault()}
    onKeyDown={(e) => e.preventDefault()}
style={{
  "--p": `${duration ? (currentTime / duration) * 100 : 0}%`,
  pointerEvents: "none",
  cursor: "default",
}}
  />
</div>

            <div className="player-controls-row">
              <div className="player-controls-left">
<button
  onClick={togglePlayPause}
  className="play-button"
  disabled={isTransitioning || isSwitchingMode || !isAudioReady || sessionEnded}
  aria-disabled={isTransitioning || isSwitchingMode || !isAudioReady || sessionEnded}
  aria-label={isPlaying ? "Pause" : "Lecture"}
>
                  <span className="play-icon">{isPlaying ? <Pause size={22} /> : <Play size={22} />}</span>
                </button>

<Tooltip
  id="breathing-mute"
  text="Coupez ou réactivez le guide respiratoire ici, à tout moment."
  placement="bottom"
  priority={40}
  showDelay={1000}
  enabled={canShowPlayerTooltips}
  sequence="player-guidance"
  order={2}
  maxWidth={280}
>
                  <button
                    onClick={toggleBreathingMute}
                    className={`icon-btn ${isBreathingMuted ? "is-muted" : ""}`}
                    aria-label={isBreathingMuted ? "Activer respiration" : "Couper respiration"}
                    aria-pressed={!isBreathingMuted}
                  >
                    <Wind size={18} />
                  </button>
                </Tooltip>

<div className="volume-wrap">
<Tooltip
  id="base-volume-memory"
  text="Ajustez ici le volume de la musique et des ondes."
  placement="bottom"
  priority={30}
  showDelay={1000}
  enabled={canShowPlayerTooltips}
  sequence="player-guidance"
  order={3}
  maxWidth={280}
>
    <button
      ref={baseVolumeBtnRef}
      onClick={() => setShowBaseVolume((v) => !v)}
      className={`icon-btn ${showBaseVolume ? "active" : ""}`}
      aria-label="Volume"
      aria-expanded={showBaseVolume}
    >
      <Volume2 size={18} />
    </button>
  </Tooltip>

                  {showBaseVolume && (
                    <div ref={baseVolumePopoverRef} className="volume-popover">
                      <div className="volume-vertical-shell">
                        <input
                          key={user?.uid || "guest"}
                          className="volume-vertical"
                          type="range"
                          min={0}
                          max={1}
                          step={0.01}
                          defaultValue={baseVolume}
                          onInput={handleBaseVolumeInput}
                          onPointerUp={commitBaseVolume}
                          onTouchEnd={commitBaseVolume}
                          onKeyUp={commitBaseVolume}
                          onBlur={commitBaseVolume}
                          aria-label="Volume général"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>


<div className="player-header player-header--minimal">
  <details className="aipl-why">
<summary className="aipl-why__summary">
  <span>Détails de la session</span>

  <svg
    className="aipl-why__chevron"
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M6 9l6 6 6-6" />
  </svg>
</summary>

<div className="aipl-why__panel">
  {!demoMode && (
    <section
      className="player-listening-mode player-listening-mode--embedded"
      aria-labelledby="player-listening-mode-title"
    >
        <div className="player-listening-mode__head">
          <div>
            <p
              id="player-listening-mode-title"
              className="player-listening-mode__eyebrow"
            >
              Ambiance sonore
            </p>

            <p className="player-listening-mode__description">
              Changez d’ambiance à tout moment pendant votre séance.
            </p>
          </div>

          {isSwitchingMode && (
            <span
              className="player-listening-mode__status"
              role="status"
              aria-live="polite"
            >
              Transition…
            </span>
          )}
        </div>

        <div
          className="player-listening-mode__switch"
          role="group"
          aria-label="Choisir l’ambiance sonore"
        >
          <button
            type="button"
            className={`player-listening-mode__button ${
              listeningWithMusic ? "is-active" : ""
            }`}
            aria-pressed={listeningWithMusic}
            disabled={
              isSwitchingMode ||
              !isAudioReady ||
              isCountingDown ||
              sessionEnded ||
              !availableListeningSources.music
            }
            onClick={() => switchListeningMode(true)}
          >
            <Music2
              size={17}
              strokeWidth={2}
              aria-hidden="true"
            />
            <span>Musique</span>
          </button>

          <button
            type="button"
            className={`player-listening-mode__button ${
              !listeningWithMusic ? "is-active" : ""
            }`}
            aria-pressed={!listeningWithMusic}
            disabled={
              isSwitchingMode ||
              !isAudioReady ||
              isCountingDown ||
              sessionEnded ||
              !availableListeningSources.noMusic
            }
            onClick={() => switchListeningMode(false)}
          >
            <Waves
              size={18}
              strokeWidth={2}
              aria-hidden="true"
            />
            <span>Ondes seules</span>
          </button>
        </div>
      </section>
  )}
      {sound?.explanation && (
        <p className="aipl-why__text">
          {sound.explanation}
        </p>
      )}

      {sound?.frequencies?.length > 0 && (
        <div
          className="aipl-why__freqs"
          aria-label="Fréquence et onde utilisées"
        >
          <div className="aipl-why__freqTitle">
            Fréquence utilisée
          </div>

          <div className="aipl-why__freqGrid">
            {sound.frequencies.map((frequency, index) => (
              <div
                key={index}
                className="aipl-why__freqCard"
              >
                <div className="aipl-why__freqItem">
                  <span>Fréquence</span>
                  <strong>{frequency.hz}</strong>
                </div>

                <div className="aipl-why__freqItem">
                  <span>Onde</span>
                  <strong>
                    {String(
                      frequency.type || "",
                    ).replace(/^Onde\s+/i, "")}
                  </strong>
                </div>

                {frequency.note && (
                  <p className="aipl-why__freqNote">
                    {frequency.note}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {sound?.adaptHint && (
        <p className="aipl-why__adapt">
          <span aria-hidden="true">🍃</span>
          {sound.adaptHint}
        </p>
      )}
    </div>
  </details>
</div>

          <div id="player-natural-sounds" className="natural-sounds-section">
            <h3 className="section-title-small">
              <span className="section-icon">🌿</span>
              Sons naturels
            </h3>
            <p className="section-description">
              Ajoutez des sons d'ambiance pour personnaliser votre expérience
            </p>

            <div className="natural-sounds-grid">
              {naturalSounds.map((ns) => {
                const active = selectedNaturalSounds.includes(ns.id);
                return (
                  <div
                    key={ns.id}
                    className={`natural-sound-item ${ns.premium && !isPremium ? "premium-locked" : ""}`}
                  >
<NaturalSoundTile
  sound={ns}
  active={active}
  loading={!!naturalLoading[ns.id]}
  onToggle={toggleNaturalSound}
  onVolumeChange={changeNaturalVolume}
  isPremium={isPremium}
  onLockedClick={() => onOpenPremium?.()}
  onGuideInteracted={() => setNaturalSoundTouched(true)}
  initialVolume={naturalVolumesRef.current[ns.id] ?? 0.7}
/>

<audio
  ref={(el) => (naturalRefs.current[ns.id] = el)}
  preload="auto"
  playsInline
  crossOrigin="anonymous"
  data-src={ns.file}
/>
                  </div>
                );
              })}
            </div>

            {selectedNaturalSounds.length > 0 && (
              <button onClick={stopAllNaturalSounds} className="stop-all-button">
                <span>🔇</span>
                <span>Désactiver tous les sons</span>
              </button>
            )}
          </div>

{!demoMode && (
  <button
    onClick={handleFinish}
    className="finish-button"
  >
    Terminer la session
  </button>
)}

          <div style={{ display: "none" }}>durationKey: {selectedDuration}</div>
        </div>
      </div>
    </div>
{replayPlayerTour && (
  <PlayerTour
    user={user}
    naturalSoundTouched={naturalSoundTouched}
    isAudioReady={isAudioReady && !isCountingDown && !isSwitchingMode}
    forceReplay={replayPlayerTour}
  />
)}
  {isBreathingFullscreen && isAudioReady && !sessionEnded && !audioError && (
    <FullscreenBreathing
      technique={sound?.id}
      isPlaying={isPlaying}
      currentTime={currentTime}
      audioRef={breathingRef}
      sound={sound}
      onExit={() => setIsBreathingFullscreen(false)}
    />
  )}

{isCountingDown && !audioError && (
  <CountdownOverlay
    isReady={isAudioReady}
    onArm={armPreparedPlayback}
    onPrime={primePreparedPlayback}
    onComplete={() => {
      if (!revealPreparedPlayback()) failPreparedPlayback();
    }}
    onSkip={() => {
      if (!revealPreparedPlayback()) failPreparedPlayback();
    }}
    onExited={() => setIsCountingDown(false)}
    onError={failPreparedPlayback}
  />
)}

{sessionEnded && !audioError && (
  demoMode ? (
    <DemoEndOverlay
      listenedSeconds={currentTime}
      onSignup={onDemoSignup}
      onLogin={onDemoLogin}
    />
  ) : (
    <EndSessionOverlay
      listenedSeconds={currentTime}
      onSaveNote={onSaveNote}
      onFinish={handleFinish}
    />
  )
)}
<style>{`
  .player-listening-mode {
    width: min(100%, 520px);
    margin: clamp(18px, 3vw, 28px) auto 0;
    padding: 15px;
    border: 1px solid rgba(97, 131, 122, 0.14);
    border-radius: 22px;
    background:
      radial-gradient(circle at 0% 0%, rgba(203, 226, 219, 0.30), transparent 58%),
      rgba(255, 255, 255, 0.72);
    box-shadow:
      0 12px 30px rgba(49, 72, 66, 0.055),
      inset 0 1px 0 rgba(255, 255, 255, 0.90);
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
  }

  .player-listening-mode__head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;
  }

  .player-listening-mode__eyebrow {
    margin: 0;
    color: #61837a;
    font-family: var(--font-body, "DM Sans", sans-serif);
    font-size: 0.70rem;
    font-weight: 850;
    line-height: 1;
    letter-spacing: 0.10em;
    text-transform: uppercase;
  }

  .player-listening-mode__description {
    max-width: 390px;
    margin: 7px 0 0;
    color: #506866;
    font-family: var(--font-body, "DM Sans", sans-serif);
    font-size: 0.82rem;
    font-weight: 500;
    line-height: 1.45;
  }

  .player-listening-mode__status {
    flex: 0 0 auto;
    padding: 6px 9px;
    border: 1px solid rgba(97, 131, 122, 0.13);
    border-radius: 999px;
    background: rgba(203, 226, 219, 0.25);
    color: #61837a;
    font-size: 0.67rem;
    font-weight: 800;
  }

  .player-listening-mode__switch {
    width: 100%;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 4px;
    padding: 4px;
    border-radius: 999px;
    background: rgba(97, 131, 122, 0.08);
  }

  .player-listening-mode__button {
    appearance: none;
    -webkit-appearance: none;
    min-width: 0;
    min-height: 44px;
    padding: 9px 12px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    border: 0;
    border-radius: 999px;
    background: transparent;
    color: rgba(80, 104, 102, 0.72);
    font-family: var(--font-body, "DM Sans", sans-serif);
    font-size: 0.80rem;
    font-weight: 750;
    line-height: 1.2;
    cursor: pointer;
    transition:
      background 180ms ease,
      color 180ms ease,
      box-shadow 180ms ease,
      transform 180ms ease,
      opacity 180ms ease;
  }

  .player-listening-mode__button.is-active {
    background: rgba(255, 255, 255, 0.96);
    color: #203e3e;
    box-shadow:
      0 7px 18px rgba(49, 72, 66, 0.09),
      inset 0 1px 0 rgba(255, 255, 255, 1);
  }

  .player-listening-mode__button:disabled {
    cursor: default;
    opacity: 0.52;
  }

  .player-listening-mode__button.is-active:disabled {
    opacity: 1;
  }

  @media (hover: hover) and (pointer: fine) {
    .player-listening-mode__button:not(:disabled):hover {
      color: #203e3e;
      transform: translateY(-1px);
    }
  }


  @media (max-width: 480px) {
    .player-listening-mode {
      width: calc(100% - 24px);
      margin-top: 18px;
      padding: 13px;
      border-radius: 20px;
    }

    .player-listening-mode__head {
      margin-bottom: 10px;
    }

    .player-listening-mode__description {
      font-size: 0.77rem;
    }

    .player-listening-mode__button {
      min-height: 42px;
      padding-inline: 8px;
      font-size: 0.74rem;
    }

    .player-listening-mode__button svg {
      width: 15px;
      height: 15px;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .player-listening-mode__button {
      transition: none !important;
    }
  }

.aipl-why__panel
  .player-listening-mode--embedded {
  width: 100%;
  margin: 0 0 20px;
  padding: 14px;

  border-radius: 18px;

  background:
    radial-gradient(
      circle at 0% 0%,
      rgba(203, 226, 219, 0.24),
      transparent 58%
    ),
    rgba(255, 255, 255, 0.48);

  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.78);
}

@media (max-width: 480px) {
  .aipl-why__panel
    .player-listening-mode--embedded {
    width: 100%;
    margin-bottom: 17px;
    padding: 12px;
  }
}

.eso-note {
  width: 100%;
  margin-top: -4px;
}

.eso-note-toggle {
  border: 0;
  padding: 6px 10px;
  background: transparent;
  color: #61837a;
  font-family: var(--font-sans);
  font-size: 12.5px;
  font-weight: 700;
  cursor: pointer;
}

.eso-note-toggle:hover {
  text-decoration: underline;
  text-underline-offset: 3px;
}

.eso-note-box {
  width: 100%;
  margin-top: 8px;
  padding: 12px;
  display: grid;
  gap: 8px;
  text-align: left;
  border-radius: 14px;
  border: 1px solid rgba(118, 165, 167, 0.18);
  background: rgba(203, 226, 219, 0.12);
  box-sizing: border-box;
  animation: esoNoteOpen 180ms ease both;
}

.eso-note-box label {
  color: rgba(54, 73, 91, 0.68);
  font-size: 11.5px;
  font-weight: 700;
  line-height: 1.4;
}

.eso-note-box textarea {
  width: 100%;
  min-height: 76px;
  padding: 10px 11px;
  resize: vertical;
  box-sizing: border-box;
  border: 1px solid rgba(118, 165, 167, 0.24);
  border-radius: 11px;
  outline: none;
  background: rgba(255, 255, 255, 0.88);
  color: #36495b;
  font-family: var(--font-sans);
  font-size: 13px;
  line-height: 1.5;
}

.eso-note-box textarea:focus {
  border-color: #76a5a7;
  box-shadow: 0 0 0 3px rgba(118, 165, 167, 0.12);
}

.eso-note-save {
  justify-self: end;
  padding: 8px 12px;
  border: 1px solid rgba(97, 131, 122, 0.24);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.7);
  color: #61837a;
  font-family: var(--font-sans);
  font-size: 11.5px;
  font-weight: 800;
  cursor: pointer;
}

.eso-note-save:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.eso-note-saved {
  padding: 9px 12px;
  border-radius: 12px;
  color: #4e7469;
  background: rgba(203, 226, 219, 0.22);
  font-size: 12px;
  font-weight: 700;
}

.eso-note-error {
  margin: 0;
  color: #a64f4f;
  font-size: 11px;
  font-weight: 600;
}

@keyframes esoNoteOpen {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}
`}</style>

<BadgeCelebration
  badge={lastBadge}
  onClose={resetLastBadge}
/>
</>
);
}
